import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
 Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

/* ============================================================
   MESYAT HOTEL PRO
   OTEL YÖNETİM PANELİ

   Backend bağlantısı:
   /api/hotel

   Özellikler:
   - Türkçe / İngilizce / Almanca
   - Mobil uyumlu
   - Animasyonlu
   - Rezervasyon yönetimi
   - Doluluk takvimi
   - Oda ve oda tipi yönetimi
   - Misafir CRM
   - Check-in / Check-out
   - Housekeeping
   - Teknik servis
   - Folyo ve ödeme
   - Otel raporları
   - Otel ayarları
============================================================ */

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://api.haymestasarim.com/api";

const STOCK_API_BASE =
  process.env.REACT_APP_STOCK_API_BASE ||
  "https://panel.haymestasarim.com/api";

const COLORS = {
  blue: "#0B4F8A",
  blueDark: "#07365E",
  blueLight: "#EAF4FF",

  orange: "#F97316",
  orangeLight: "#FFF1E7",

  green: "#16A34A",
  greenLight: "#EAF8EF",

  red: "#DC2626",
  redLight: "#FDECEC",

  amber: "#D97706",
  amberLight: "#FFF5DD",

  purple: "#7C3AED",
  purpleLight: "#F3ECFF",

  background: "#F4F7FB",
  surface: "#FFFFFF",

  text: "#172033",
  muted: "#64748B",

  border: "rgba(15, 23, 42, 0.10)",
};

const TRANSLATIONS = {
  tr: {
    title: "MesYat Hotel Pro",

    subtitle:
      "Rezervasyon, ön büro, oda, misafir, housekeeping ve finans yönetimi",

    dashboard: "Genel Bakış",
    calendar: "Doluluk Takvimi",
    reservations: "Rezervasyonlar",
    rooms: "Odalar",
    guests: "Misafirler",
    housekeeping: "Kat Hizmetleri",
    maintenance: "Teknik Servis",
    finance: "Folyo ve Ödemeler",
    reports: "Raporlar",
    settings: "Otel Ayarları",

    refresh: "Yenile",
    add: "Yeni Ekle",
    save: "Kaydet",
    update: "Güncelle",
    cancel: "Vazgeç",
    close: "Kapat",
    edit: "Düzenle",
    search: "Ara",
    actions: "İşlemler",
    status: "Durum",

    noData: "Henüz kayıt yok",
    loading: "Yükleniyor",

    totalRooms: "Toplam Oda",
    occupied: "Dolu Oda",
    available: "Hazır Oda",
    dirty: "Temizlik Bekleyen",
    blockedRooms: "Bloke / Arızalı",
    occupancyRate: "Doluluk Oranı",

    arrivals: "Bugünkü Giriş",
    departures: "Bugünkü Çıkış",

    todayRevenue: "Bugünkü Gelir",
    monthRevenue: "Aylık Gelir",
    openBalance: "Açık Bakiye",

    recentReservations: "Son Rezervasyonlar",

    roomNo: "Oda No",
    roomType: "Oda Tipi",
    floor: "Kat",
    building: "Bina",

    guest: "Misafir",
    guestName: "Misafir Adı",
    firstName: "Ad",
    lastName: "Soyad",

    phone: "Telefon",
    email: "E-posta",
    country: "Ülke",
    nationality: "Uyruk",

    checkIn: "Giriş Tarihi",
    checkOut: "Çıkış Tarihi",

    adults: "Yetişkin",
    children: "Çocuk",
    infants: "Bebek",

    source: "Rezervasyon Kaynağı",
    nightlyRate: "Gecelik Fiyat",
    currency: "Para Birimi",

    specialRequests: "Özel İstekler",
    notes: "Notlar",

    reservationNo: "Rezervasyon No",
    newReservation: "Yeni Rezervasyon",
    editReservation: "Rezervasyonu Düzenle",

    checkInAction: "Check-in Yap",
    checkOutAction: "Check-out Yap",

    cancelReservation: "Rezervasyonu İptal Et",

    roomTypes: "Oda Tipleri",
    newRoom: "Yeni Oda",
    newRoomType: "Yeni Oda Tipi",

    nameTr: "Türkçe Ad",
    nameEn: "İngilizce Ad",
    nameDe: "Almanca Ad",

    code: "Kod",
    basePrice: "Başlangıç Fiyatı",
    capacity: "Kapasite",

    roomStatus: "Oda Durumu",

    newGuest: "Yeni Misafir",

    identityNo: "Kimlik No",
    passportNo: "Pasaport No",
    identityType: "Belge Türü",

    birthDate: "Doğum Tarihi",
    language: "Dil",
    vip: "VIP Seviyesi",

    preferences: "Misafir Tercihleri",
    allergies: "Alerji Bilgisi",

    stays: "Konaklama",

    task: "Görev",
    taskType: "Görev Türü",
    taskDate: "Görev Tarihi",
    priority: "Öncelik",
    assignedTo: "Atanan Personel",

    newTask: "Yeni Görev",

    newMaintenance: "Yeni Arıza Kaydı",

    titleField: "Başlık",
    category: "Kategori",
    cost: "Maliyet",

    folio: "Folyo",
    charge: "Harcama Ekle",
    payment: "Ödeme Al",

    description: "Açıklama",
    amount: "Tutar",
    paymentMethod: "Ödeme Yöntemi",

    summary: "Özet",
    total: "Toplam",
    paid: "Ödenen",
    balance: "Kalan",

    propertyName: "Otel / Tesis Adı",
    legalName: "Yasal Unvan",
    propertyCode: "Tesis Kodu",

    city: "Şehir",
    district: "İlçe",
    address: "Adres",

    website: "Web Sitesi",
    timezone: "Saat Dilimi",

    checkInTime: "Standart Giriş Saati",
    checkOutTime: "Standart Çıkış Saati",

    tax: "KDV / Vergi %",
    accommodationTax: "Konaklama Vergisi %",

    channelManager: "Kanal Yöneticisi",
    bookingEngine: "Online Rezervasyon",
    qrGuide: "Dijital Oda Rehberi",
    whatsapp: "WhatsApp Otomasyonları",

    saved: "Kayıt başarıyla tamamlandı",
    error: "İşlem tamamlanamadı",

    confirmCancel:
      "Bu rezervasyon iptal edilsin mi?",

    confirmCheckOut:
      "Ödenmemiş bakiye varsa çıkış engellenecektir. Devam edilsin mi?",

    all: "Tümü",

    inquiry: "Talep",
    option: "Opsiyon",
    pending: "Bekliyor",
    confirmed: "Onaylı",
    checked_in: "Konaklıyor",
    checked_out: "Çıkış Yaptı",
    cancelled: "İptal",
    no_show: "Gelmedi",

    vacant_clean: "Boş / Temiz",
    vacant_dirty: "Boş / Kirli",

    occupied_clean: "Dolu / Temiz",
    occupied_dirty: "Dolu / Kirli",

    inspected: "Kontrol Edildi",

    maintenanceStatus: "Bakımda",

    out_of_order: "Arızalı",
    out_of_service: "Servis Dışı",
    blocked: "Bloke",
    do_not_disturb: "Rahatsız Etmeyin",

    clean: "Temiz",
    dirtyStatus: "Kirli",

    low: "Düşük",
    normal: "Normal",
    high: "Yüksek",
    urgent: "Acil",

    open: "Açık",
    assigned: "Atandı",
    in_progress: "Devam Ediyor",
    waiting_part: "Parça Bekliyor",
    completed: "Tamamlandı",

    cash: "Nakit",
    card: "Kredi Kartı",
    transfer: "Havale / EFT",
    online: "Online",
    deposit: "Depozito",
    virtual_card: "Sanal Kart",
    open_account: "Açık Hesap",
    other: "Diğer",

    direct: "Doğrudan",
    websiteSource: "Web Sitesi",
    walk_in: "Kapı Müşterisi",
    booking: "Booking.com",
    airbnb: "Airbnb",
    expedia: "Expedia",
    agency: "Acenta",
    company: "Firma",
    phoneSource: "Telefon",

    stayover_cleaning:
      "Konaklama Temizliği",

    checkout_cleaning:
      "Çıkış Temizliği",

    deep_cleaning:
      "Detaylı Temizlik",

    inspection:
      "Oda Kontrolü",

    minibar:
      "Minibar Kontrolü",

    linen:
      "Çarşaf / Havlu",

    vip_setup:
      "VIP Oda Hazırlığı",

    extra_bed:
      "Ek Yatak",

    baby_bed:
      "Bebek Yatağı",

    collectedRevenue:
      "Toplanan Gelir",

    averageStay:
      "Ortalama Geceleme",

    totalReservations:
      "Toplam Rezervasyon",

    cancellationRate:
      "İptal Oranı",

    roomNights:
      "Satılan Oda Gecesi",

    reservationRequired:
      "Misafir, oda tipi ve tarihler zorunludur",

    guestInviteButton:
      "Misafir Kodu Oluştur",

    guestInviteTitle:
      "Misafir Uygulama Daveti",

    guestInviteDescription:
      "Bu rezervasyona özel, yalnızca rakamlardan oluşan 5 haneli tek kullanımlık kod ve QR oluşturun.",

    guestInviteGenerate:
      "5 Haneli Kod ve QR Oluştur",

    guestInviteGenerating:
      "Kod oluşturuluyor...",

    guestInviteCode:
      "Tek Kullanımlık Davet Kodu",

    guestInviteLink:
      "Uygulama Bağlantısı",

    guestInviteExpires:
      "Son Kullanım",

    guestInviteSecurityNote:
      "Güvenlik nedeniyle kod yalnızca bu pencerede gösterilir. Pencereyi kapatmadan önce kodu kopyalayın veya misafire gönderin.",

    guestInviteQr:
      "Misafir bu karekodu kullanabilir veya 5 haneli kodu uygulamaya yazabilir.",

    guestInviteCreated:
      "Misafir davet kodu güvenli şekilde oluşturuldu",

    guestInviteCopyCode:
      "Kodu Kopyala",

    guestInviteCopyLink:
      "Bağlantıyı Kopyala",

    guestInviteCopied:
      "Panoya kopyalandı",

    guestInviteWhatsApp:
      "WhatsApp ile Gönder",

    guestInviteReservation:
      "Rezervasyon",

    guestInviteGuest:
      "Misafir",

    guestInviteRoom:
      "Oda",

    guestInviteNotAllowed:
      "İptal edilmiş, gelmeyen veya çıkışı tamamlanmış rezervasyonlar için davet oluşturulamaz.",

    guestInviteShareIntro:
      "Konaklamanızı MesYat uygulamasına bağlamak için aşağıdaki tek kullanımlık kodu girin.",

    selectGuest:
      "Misafir Seç",

    noPhysicalRoom:
      "Fiziksel oda daha sonra atanabilir",

    paymentBlocked:
      "Açık bakiye nedeniyle check-out yapılamadı",

    active:
      "Aktif",

    inactive:
      "Pasif",

    success:
      "Başarılı",

    hotelInformation:
      "Otel Bilgileri",

    moduleSettings:
      "Modül Ayarları",

    occupancy:
      "Doluluk",

    adr:
      "ADR",

    revpar:
      "RevPAR",

    roomCount:
      "Oda Sayısı",

    guestHistory:
      "Misafir Geçmişi",

    document:
      "Belge",

    roomReady:
      "Oda Hazır",

    cleaningPending:
      "Temizlik Bekliyor",

    roomBlocked:
      "Odayı Bakıma Kapat",

    estimatedCost:
      "Tahmini Maliyet",

    actualCost:
      "Gerçekleşen Maliyet",

    reportPeriod:
      "Rapor Dönemi",
  },

  en: {
    title: "MesYat Hotel Pro",

    subtitle:
      "Reservations, front office, rooms, guests, housekeeping and finance",

    dashboard: "Overview",
    calendar: "Occupancy Calendar",
    reservations: "Reservations",
    rooms: "Rooms",
    guests: "Guests",
    housekeeping: "Housekeeping",
    maintenance: "Maintenance",
    finance: "Folio and Payments",
    reports: "Reports",
    settings: "Hotel Settings",

    refresh: "Refresh",
    add: "Add New",
    save: "Save",
    update: "Update",
    cancel: "Cancel",
    close: "Close",
    edit: "Edit",
    search: "Search",
    actions: "Actions",
    status: "Status",

    noData: "No records yet",
    loading: "Loading",

    totalRooms: "Total Rooms",
    occupied: "Occupied Rooms",
    available: "Ready Rooms",
    dirty: "Cleaning Required",
    blockedRooms: "Blocked / Out of Order",
    occupancyRate: "Occupancy Rate",

    arrivals: "Today's Arrivals",
    departures: "Today's Departures",

    todayRevenue: "Today's Revenue",
    monthRevenue: "Monthly Revenue",
    openBalance: "Open Balance",

    recentReservations: "Recent Reservations",

    roomNo: "Room No",
    roomType: "Room Type",
    floor: "Floor",
    building: "Building",

    guest: "Guest",
    guestName: "Guest Name",
    firstName: "First Name",
    lastName: "Last Name",

    phone: "Phone",
    email: "Email",
    country: "Country",
    nationality: "Nationality",

    checkIn: "Check-in Date",
    checkOut: "Check-out Date",

    adults: "Adults",
    children: "Children",
    infants: "Infants",

    source: "Reservation Source",
    nightlyRate: "Nightly Rate",
    currency: "Currency",

    specialRequests: "Special Requests",
    notes: "Notes",

    reservationNo: "Reservation No",
    newReservation: "New Reservation",
    editReservation: "Edit Reservation",

    checkInAction: "Check-in",
    checkOutAction: "Check-out",

    cancelReservation: "Cancel Reservation",

    roomTypes: "Room Types",
    newRoom: "New Room",
    newRoomType: "New Room Type",

    nameTr: "Turkish Name",
    nameEn: "English Name",
    nameDe: "German Name",

    code: "Code",
    basePrice: "Base Price",
    capacity: "Capacity",

    roomStatus: "Room Status",

    newGuest: "New Guest",

    identityNo: "Identity No",
    passportNo: "Passport No",
    identityType: "Document Type",

    birthDate: "Birth Date",
    language: "Language",
    vip: "VIP Level",

    preferences: "Guest Preferences",
    allergies: "Allergy Information",

    stays: "Stays",

    task: "Task",
    taskType: "Task Type",
    taskDate: "Task Date",
    priority: "Priority",
    assignedTo: "Assigned Staff",

    newTask: "New Task",

    newMaintenance: "New Maintenance Issue",

    titleField: "Title",
    category: "Category",
    cost: "Cost",

    folio: "Folio",
    charge: "Add Charge",
    payment: "Take Payment",

    description: "Description",
    amount: "Amount",
    paymentMethod: "Payment Method",

    summary: "Summary",
    total: "Total",
    paid: "Paid",
    balance: "Balance",

    propertyName: "Hotel / Property Name",
    legalName: "Legal Name",
    propertyCode: "Property Code",

    city: "City",
    district: "District",
    address: "Address",

    website: "Website",
    timezone: "Timezone",

    checkInTime: "Default Check-in Time",
    checkOutTime: "Default Check-out Time",

    tax: "Tax %",
    accommodationTax: "Accommodation Tax %",

    channelManager: "Channel Manager",
    bookingEngine: "Online Booking",
    qrGuide: "Digital Room Guide",
    whatsapp: "WhatsApp Automations",

    saved: "Saved successfully",
    error: "Operation failed",

    confirmCancel:
      "Cancel this reservation?",

    confirmCheckOut:
      "Checkout will be blocked if there is an unpaid balance. Continue?",

    all: "All",

    inquiry: "Inquiry",
    option: "Option",
    pending: "Pending",
    confirmed: "Confirmed",
    checked_in: "In House",
    checked_out: "Checked Out",
    cancelled: "Cancelled",
    no_show: "No-show",

    vacant_clean: "Vacant / Clean",
    vacant_dirty: "Vacant / Dirty",

    occupied_clean: "Occupied / Clean",
    occupied_dirty: "Occupied / Dirty",

    inspected: "Inspected",

    maintenanceStatus: "Maintenance",

    out_of_order: "Out of Order",
    out_of_service: "Out of Service",
    blocked: "Blocked",
    do_not_disturb: "Do Not Disturb",

    clean: "Clean",
    dirtyStatus: "Dirty",

    low: "Low",
    normal: "Normal",
    high: "High",
    urgent: "Urgent",

    open: "Open",
    assigned: "Assigned",
    in_progress: "In Progress",
    waiting_part: "Waiting for Part",
    completed: "Completed",

    cash: "Cash",
    card: "Credit Card",
    transfer: "Bank Transfer",
    online: "Online",
    deposit: "Deposit",
    virtual_card: "Virtual Card",
    open_account: "Open Account",
    other: "Other",

    direct: "Direct",
    websiteSource: "Website",
    walk_in: "Walk-in",
    booking: "Booking.com",
    airbnb: "Airbnb",
    expedia: "Expedia",
    agency: "Agency",
    company: "Company",
    phoneSource: "Phone",

    stayover_cleaning:
      "Stayover Cleaning",

    checkout_cleaning:
      "Checkout Cleaning",

    deep_cleaning:
      "Deep Cleaning",

    inspection:
      "Room Inspection",

    minibar:
      "Minibar Check",

    linen:
      "Linen / Towels",

    vip_setup:
      "VIP Room Setup",

    extra_bed:
      "Extra Bed",

    baby_bed:
      "Baby Cot",

    collectedRevenue:
      "Collected Revenue",

    averageStay:
      "Average Stay",

    totalReservations:
      "Total Reservations",

    cancellationRate:
      "Cancellation Rate",

    roomNights:
      "Room Nights",

    reservationRequired:
      "Guest, room type and dates are required",

    guestInviteButton:
      "Create Guest Code",

    guestInviteTitle:
      "Guest App Invitation",

    guestInviteDescription:
      "Create a reservation-specific, numeric 5-digit one-time code and QR access.",

    guestInviteGenerate:
      "Create 5-Digit Code & QR",

    guestInviteGenerating:
      "Creating code...",

    guestInviteCode:
      "One-Time Invitation Code",

    guestInviteLink:
      "App Link",

    guestInviteExpires:
      "Expires",

    guestInviteSecurityNote:
      "For security, the code is shown only in this window. Copy it or send it to the guest before closing the window.",

    guestInviteQr:
      "The guest can use this QR or enter the 5-digit code in the MesYat app.",

    guestInviteCreated:
      "Guest invitation code created securely",

    guestInviteCopyCode:
      "Copy Code",

    guestInviteCopyLink:
      "Copy Link",

    guestInviteCopied:
      "Copied to clipboard",

    guestInviteWhatsApp:
      "Send via WhatsApp",

    guestInviteReservation:
      "Reservation",

    guestInviteGuest:
      "Guest",

    guestInviteRoom:
      "Room",

    guestInviteNotAllowed:
      "Invitations cannot be created for cancelled, no-show or checked-out reservations.",

    guestInviteShareIntro:
      "Enter the one-time code below to connect your stay to the MesYat app.",

    selectGuest:
      "Select Guest",

    noPhysicalRoom:
      "Physical room can be assigned later",

    paymentBlocked:
      "Checkout failed due to unpaid balance",

    active:
      "Active",

    inactive:
      "Inactive",

    success:
      "Success",

    hotelInformation:
      "Hotel Information",

    moduleSettings:
      "Module Settings",

    occupancy:
      "Occupancy",

    adr:
      "ADR",

    revpar:
      "RevPAR",

    roomCount:
      "Room Count",

    guestHistory:
      "Guest History",

    document:
      "Document",

    roomReady:
      "Room Ready",

    cleaningPending:
      "Cleaning Pending",

    roomBlocked:
      "Block Room for Maintenance",

    estimatedCost:
      "Estimated Cost",

    actualCost:
      "Actual Cost",

    reportPeriod:
      "Report Period",
  },

  de: {
    title: "MesYat Hotel Pro",

    subtitle:
      "Reservierungen, Rezeption, Zimmer, Gäste, Housekeeping und Finanzen",

    dashboard: "Übersicht",
    calendar: "Belegungskalender",
    reservations: "Reservierungen",
    rooms: "Zimmer",
    guests: "Gäste",
    housekeeping: "Housekeeping",
    maintenance: "Technischer Dienst",
    finance: "Folio und Zahlungen",
    reports: "Berichte",
    settings: "Hoteleinstellungen",

    refresh: "Aktualisieren",
    add: "Neu hinzufügen",
    save: "Speichern",
    update: "Aktualisieren",
    cancel: "Abbrechen",
    close: "Schließen",
    edit: "Bearbeiten",
    search: "Suchen",
    actions: "Aktionen",
    status: "Status",

    noData: "Noch keine Einträge",
    loading: "Wird geladen",

    totalRooms: "Zimmer gesamt",
    occupied: "Belegte Zimmer",
    available: "Bereite Zimmer",
    dirty: "Reinigung erforderlich",
    blockedRooms: "Gesperrt / Defekt",
    occupancyRate: "Auslastung",

    arrivals: "Heutige Anreisen",
    departures: "Heutige Abreisen",

    todayRevenue: "Heutiger Umsatz",
    monthRevenue: "Monatsumsatz",
    openBalance: "Offener Saldo",

    recentReservations: "Letzte Reservierungen",

    roomNo: "Zimmernummer",
    roomType: "Zimmertyp",
    floor: "Etage",
    building: "Gebäude",

    guest: "Gast",
    guestName: "Gastname",
    firstName: "Vorname",
    lastName: "Nachname",

    phone: "Telefon",
    email: "E-Mail",
    country: "Land",
    nationality: "Nationalität",

    checkIn: "Anreise",
    checkOut: "Abreise",

    adults: "Erwachsene",
    children: "Kinder",
    infants: "Babys",

    source: "Reservierungsquelle",
    nightlyRate: "Preis pro Nacht",
    currency: "Währung",

    specialRequests: "Sonderwünsche",
    notes: "Notizen",

    reservationNo: "Reservierungsnummer",
    newReservation: "Neue Reservierung",
    editReservation: "Reservierung bearbeiten",

    checkInAction: "Check-in",
    checkOutAction: "Check-out",

    cancelReservation: "Reservierung stornieren",

    roomTypes: "Zimmertypen",
    newRoom: "Neues Zimmer",
    newRoomType: "Neuer Zimmertyp",

    nameTr: "Türkischer Name",
    nameEn: "Englischer Name",
    nameDe: "Deutscher Name",

    code: "Code",
    basePrice: "Grundpreis",
    capacity: "Kapazität",

    roomStatus: "Zimmerstatus",

    newGuest: "Neuer Gast",

    identityNo: "Ausweisnummer",
    passportNo: "Passnummer",
    identityType: "Dokumenttyp",

    birthDate: "Geburtsdatum",
    language: "Sprache",
    vip: "VIP-Stufe",

    preferences: "Gastpräferenzen",
    allergies: "Allergieinformationen",

    stays: "Aufenthalte",

    task: "Aufgabe",
    taskType: "Aufgabentyp",
    taskDate: "Aufgabendatum",
    priority: "Priorität",
    assignedTo: "Zugewiesener Mitarbeiter",

    newTask: "Neue Aufgabe",

    newMaintenance: "Neue Störung",

    titleField: "Titel",
    category: "Kategorie",
    cost: "Kosten",

    folio: "Folio",
    charge: "Belastung hinzufügen",
    payment: "Zahlung erfassen",

    description: "Beschreibung",
    amount: "Betrag",
    paymentMethod: "Zahlungsart",

    summary: "Zusammenfassung",
    total: "Gesamt",
    paid: "Bezahlt",
    balance: "Restbetrag",

    propertyName: "Hotel / Unterkunft",
    legalName: "Rechtlicher Name",
    propertyCode: "Objektcode",

    city: "Stadt",
    district: "Bezirk",
    address: "Adresse",

    website: "Webseite",
    timezone: "Zeitzone",

    checkInTime: "Standard-Check-in",
    checkOutTime: "Standard-Check-out",

    tax: "Steuer %",
    accommodationTax: "Beherbergungssteuer %",

    channelManager: "Channel Manager",
    bookingEngine: "Online-Buchung",
    qrGuide: "Digitaler Zimmerführer",
    whatsapp: "WhatsApp-Automatisierungen",

    saved: "Erfolgreich gespeichert",
    error: "Vorgang fehlgeschlagen",

    confirmCancel:
      "Diese Reservierung stornieren?",

    confirmCheckOut:
      "Bei offenem Saldo wird der Check-out blockiert. Fortfahren?",

    all: "Alle",

    inquiry: "Anfrage",
    option: "Option",
    pending: "Wartend",
    confirmed: "Bestätigt",
    checked_in: "Im Haus",
    checked_out: "Abgereist",
    cancelled: "Storniert",
    no_show: "Nicht erschienen",

    vacant_clean: "Frei / Sauber",
    vacant_dirty: "Frei / Schmutzig",

    occupied_clean: "Belegt / Sauber",
    occupied_dirty: "Belegt / Schmutzig",

    inspected: "Kontrolliert",

    maintenanceStatus: "Wartung",

    out_of_order: "Defekt",
    out_of_service: "Außer Betrieb",
    blocked: "Gesperrt",
    do_not_disturb: "Bitte nicht stören",

    clean: "Sauber",
    dirtyStatus: "Schmutzig",

    low: "Niedrig",
    normal: "Normal",
    high: "Hoch",
    urgent: "Dringend",

    open: "Offen",
    assigned: "Zugewiesen",
    in_progress: "In Bearbeitung",
    waiting_part: "Wartet auf Ersatzteil",
    completed: "Abgeschlossen",

    cash: "Bar",
    card: "Kreditkarte",
    transfer: "Überweisung",
    online: "Online",
    deposit: "Kaution",
    virtual_card: "Virtuelle Karte",
    open_account: "Offene Rechnung",
    other: "Andere",

    direct: "Direkt",
    websiteSource: "Webseite",
    walk_in: "Walk-in",
    booking: "Booking.com",
    airbnb: "Airbnb",
    expedia: "Expedia",
    agency: "Agentur",
    company: "Firma",
    phoneSource: "Telefon",

    stayover_cleaning:
      "Aufenthaltsreinigung",

    checkout_cleaning:
      "Abreisereinigung",

    deep_cleaning:
      "Grundreinigung",

    inspection:
      "Zimmerkontrolle",

    minibar:
      "Minibar-Kontrolle",

    linen:
      "Bettwäsche / Handtücher",

    vip_setup:
      "VIP-Zimmervorbereitung",

    extra_bed:
      "Zusatzbett",

    baby_bed:
      "Babybett",

    collectedRevenue:
      "Eingenommener Umsatz",

    averageStay:
      "Durchschnittlicher Aufenthalt",

    totalReservations:
      "Reservierungen gesamt",

    cancellationRate:
      "Stornierungsrate",

    roomNights:
      "Zimmernächte",

    reservationRequired:
      "Gast, Zimmertyp und Daten sind erforderlich",

    guestInviteButton:
      "Gastcode erstellen",

    guestInviteTitle:
      "Einladung zur Gäste-App",

    guestInviteDescription:
      "Erstellen Sie einen reservierungsspezifischen, einmaligen 5-stelligen Zahlencode mit QR-Zugang.",

    guestInviteGenerate:
      "5-stelligen Code und QR erstellen",

    guestInviteGenerating:
      "Code wird erstellt...",

    guestInviteCode:
      "Einmaliger Einladungscode",

    guestInviteLink:
      "App-Link",

    guestInviteExpires:
      "Gültig bis",

    guestInviteSecurityNote:
      "Aus Sicherheitsgründen wird der Code nur in diesem Fenster angezeigt. Kopieren oder senden Sie ihn vor dem Schließen an den Gast.",

    guestInviteQr:
      "Der Gast kann diesen QR-Code verwenden oder den 5-stelligen Code in der MesYat-App eingeben.",

    guestInviteCreated:
      "Gäste-Einladungscode wurde sicher erstellt",

    guestInviteCopyCode:
      "Code kopieren",

    guestInviteCopyLink:
      "Link kopieren",

    guestInviteCopied:
      "In die Zwischenablage kopiert",

    guestInviteWhatsApp:
      "Per WhatsApp senden",

    guestInviteReservation:
      "Reservierung",

    guestInviteGuest:
      "Gast",

    guestInviteRoom:
      "Zimmer",

    guestInviteNotAllowed:
      "Für stornierte, nicht erschienene oder bereits ausgecheckte Reservierungen kann keine Einladung erstellt werden.",

    guestInviteShareIntro:
      "Geben Sie den folgenden einmaligen Code ein, um Ihren Aufenthalt mit der MesYat-App zu verbinden.",

    selectGuest:
      "Gast auswählen",

    noPhysicalRoom:
      "Das konkrete Zimmer kann später zugewiesen werden",

    paymentBlocked:
      "Check-out wegen offenem Saldo fehlgeschlagen",

    active:
      "Aktiv",

    inactive:
      "Inaktiv",

    success:
      "Erfolgreich",

    hotelInformation:
      "Hotelinformationen",

    moduleSettings:
      "Moduleinstellungen",

    occupancy:
      "Auslastung",

    adr:
      "ADR",

    revpar:
      "RevPAR",

    roomCount:
      "Zimmeranzahl",

    guestHistory:
      "Gasthistorie",

    document:
      "Dokument",

    roomReady:
      "Zimmer bereit",

    cleaningPending:
      "Reinigung ausstehend",

    roomBlocked:
      "Zimmer für Wartung sperren",

    estimatedCost:
      "Geschätzte Kosten",

    actualCost:
      "Tatsächliche Kosten",

    reportPeriod:
      "Berichtszeitraum",
  },
};

const ROOM_STATUSES = [
  "vacant_clean",
  "vacant_dirty",
  "occupied_clean",
  "occupied_dirty",
  "inspected",
  "maintenance",
  "out_of_order",
  "out_of_service",
  "blocked",
  "do_not_disturb",
];

const RESERVATION_STATUSES = [
  "inquiry",
  "option",
  "pending",
  "confirmed",
  "checked_in",
  "checked_out",
  "cancelled",
  "no_show",
];

const RESERVATION_SOURCES = [
  "direct",
  "website",
  "walk_in",
  "booking",
  "airbnb",
  "expedia",
  "agency",
  "company",
  "phone",
  "other",
];

const PRIORITIES = [
  "low",
  "normal",
  "high",
  "urgent",
];

const HOUSEKEEPING_TYPES = [
  "stayover_cleaning",
  "checkout_cleaning",
  "deep_cleaning",
  "inspection",
  "minibar",
  "linen",
  "vip_setup",
  "extra_bed",
  "baby_bed",
  "other",
];

const PAYMENT_METHODS = [
  "cash",
  "card",
  "transfer",
  "online",
  "deposit",
  "virtual_card",
  "open_account",
  "other",
];

const CURRENCIES = [
  "TRY",
  "EUR",
  "USD",
  "CHF",
  "GBP",
];

const LANGUAGES = [
  "tr",
  "en",
  "de",
];

const tokenHeaders = () => ({
  Authorization:
    `Bearer ${localStorage.getItem("token") || ""}`,

  "Content-Type":
    "application/json",

  Accept:
    "application/json",
});

const isoDate = (
  date = new Date()
) =>
  date
    .toISOString()
    .slice(0, 10);

const addDays = (
  dateText,
  days
) => {
  const date =
    new Date(
      `${dateText}T12:00:00`
    );

  date.setDate(
    date.getDate() + days
  );

  return isoDate(date);
};

const normalizeDate = (
  value
) => {
  if (!value) {
    return "";
  }

  return String(value)
    .slice(0, 10);
};

const formatDateTime = (
  value,
  lang = "tr"
) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  const locale =
    lang === "de"
      ? "de-DE"
      : lang === "en"
      ? "en-US"
      : "tr-TR";

  return new Intl.DateTimeFormat(
    locale,
    {
      dateStyle: "medium",
      timeStyle: "short",
    }
  ).format(date);
};

const copyTextToClipboard = async (
  value
) => {
  const text = String(value || "");

  if (!text) {
    return false;
  }

  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // HTTPS/izin kisitinda eski tarayici yontemine guvenli geri donus.
    }
  }

  if (typeof document === "undefined") {
    return false;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
};

const normalizeWhatsAppPhone = (
  value
) => {
  let digits = String(value || "")
    .replace(/\D/g, "");

  if (digits.startsWith("00")) {
    digits = digits.slice(2);
  }

  if (
    digits.length === 11 &&
    digits.startsWith("0")
  ) {
    digits = `90${digits.slice(1)}`;
  } else if (
    digits.length === 10 &&
    digits.startsWith("5")
  ) {
    digits = `90${digits}`;
  }

  return digits;
};

const numberValue = (
  value,
  fallback = 0
) => {
  const number =
    Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
};

const money = (
  value,
  currency = "TRY",
  lang = "tr"
) => {
  const locale =
    lang === "de"
      ? "de-DE"
      : lang === "en"
      ? "en-US"
      : "tr-TR";

  try {
    return new Intl.NumberFormat(
      locale,
      {
        style: "currency",
        currency:
          currency || "TRY",

        maximumFractionDigits: 2,
      }
    ).format(
      Number(value || 0)
    );
  } catch {
    return `${Number(value || 0).toFixed(2)} ${currency || ""}`;
  }
};









function ResponsiveGrid({
  container = false,
  xs = 12,
  sm,
  md,
  lg,
  spacing = 0,
  children,
  sx = {},
  ...props
}) {
  const createSpan = (value) => {
    const safeValue = Math.max(
      1,
      Math.min(12, Number(value || 12))
    );

    return `span ${safeValue} / span ${safeValue}`;
  };

  if (container) {
    return (
      <Box
        {...props}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,

          display: "grid",

          gridTemplateColumns:
            "repeat(12, minmax(0, 1fr))",

          alignItems: "stretch",

          gap: (theme) =>
            theme.spacing(spacing),

          boxSizing: "border-box",

          "& > *": {
            minWidth: 0,
            maxWidth: "100%",
          },

          ...sx,
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      {...props}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,

        boxSizing: "border-box",

        gridColumn: {
          xs: createSpan(xs),
          sm: createSpan(sm ?? xs),
          md: createSpan(md ?? sm ?? xs),
          lg: createSpan(lg ?? md ?? sm ?? xs),
        },

        "@media (max-width:599px)": {
          gridColumn: "span 12 / span 12",
        },

        ...sx,
      }}
    >
      {children}
    </Box>
  );
}





const cardStyle = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,

  boxSizing: "border-box",

  borderRadius: {
    xs: 3,
    sm: 4,
  },

  border:
    `1px solid ${COLORS.border}`,

  boxShadow:
    "0 14px 40px rgba(15, 23, 42, 0.07)",

  overflow: "hidden",

  "& .MuiCardContent-root": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,

    boxSizing: "border-box",

    padding: {
      xs: 1.5,
      sm: 2,
    },

    "&:last-child": {
      paddingBottom: {
        xs: 1.5,
        sm: 2,
      },
    },
  },
};

const inputStyle = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,

  "& .MuiOutlinedInput-root": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,

    borderRadius: 3,

    backgroundColor:
      COLORS.surface,

    boxSizing: "border-box",
  },

  "& .MuiInputBase-input": {
    minWidth: 0,
  },
};

function statusColor(
  status
) {
  if (
    [
      "confirmed",
      "checked_in",
      "vacant_clean",
      "inspected",
      "completed",
      "paid",
      "clean",
    ].includes(status)
  ) {
    return "success";
  }

  if (
    [
      "cancelled",
      "out_of_order",
      "out_of_service",
      "urgent",
      "no_show",
    ].includes(status)
  ) {
    return "error";
  }

  if (
    [
      "pending",
      "option",
      "vacant_dirty",
      "high",
      "open",
      "waiting_part",
    ].includes(status)
  ) {
    return "warning";
  }

  if (
    [
      "in_progress",
      "occupied_clean",
      "occupied_dirty",
      "assigned",
    ].includes(status)
  ) {
    return "info";
  }

  return "default";
}

function StatusChip({
  value,
  translate,
}) {
  const translationKey =
    value === "maintenance"
      ? "maintenanceStatus"
      : value;

  return (
    <Chip
      size="small"

      color={
        statusColor(value)
      }

      label={
        translate(
          translationKey
        )
      }

      sx={{
        fontWeight: 900,
      }}
    />
  );
}

function EmptyState({
  translate,
}) {
  return (
    <Box
      sx={{
        padding: 5,

        textAlign:
          "center",

        color:
          COLORS.muted,
      }}
    >
      <Typography
        sx={{
          fontSize: 44,
          marginBottom: 1,
        }}
      >
        🏨
      </Typography>

      <Typography
        fontWeight={900}
      >
        {translate("noData")}
      </Typography>
    </Box>
  );
}

function SectionTitle({
  title,
  subtitle,
  action,
}) {
  return (
    <Stack
      direction={{
        xs: "column",
        sm: "row",
      }}

      justifyContent=
        "space-between"

      alignItems={{
        xs: "stretch",
        sm: "center",
      }}

      spacing={2}

      sx={{
        marginBottom: 2,
      }}
    >
      <Box
        sx={{
          minWidth: 0,
        }}
      >
        <Typography
          variant="h5"

          fontWeight={1000}

          sx={{
            color:
              COLORS.text,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              color:
                COLORS.muted,

              fontSize: 14,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {action}
    </Stack>
  );
}


function clampDashboardPercent(value) {
  const numericValue =
    Number(value || 0);

  if (
    !Number.isFinite(
      numericValue
    )
  ) {
    return 0;
  }

  return Math.max(
    0,
    Math.min(
      100,
      numericValue
    )
  );
}

function DashboardProgressRow({
  icon,
  label,
  value,
  total,
  accent = COLORS.blue,
}) {
  const numericValue =
    Math.max(
      0,
      numberValue(
        value,
        0
      )
    );

  const numericTotal =
    Math.max(
      0,
      numberValue(
        total,
        0
      )
    );

  const percent =
    numericTotal > 0
      ? clampDashboardPercent(
          (
            numericValue /
            numericTotal
          ) * 100
        )
      : 0;

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          marginBottom: 0.75,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.8}
          sx={{
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,

              borderRadius: 2,

              display: "grid",
              placeItems: "center",

              backgroundColor:
                `${accent}28`,

              fontSize: 14,
            }}
          >
            {icon}
          </Box>

          <Typography
            sx={{
              minWidth: 0,

              color:
                "rgba(255,255,255,0.82)",

              fontSize: 12.5,
              fontWeight: 800,
              lineHeight: 1.25,
            }}
          >
            {label}
          </Typography>
        </Stack>

        <Typography
          sx={{
            flexShrink: 0,

            color: "#FFFFFF",

            fontWeight: 1000,

            fontVariantNumeric:
              "tabular-nums",
          }}
        >
          {numericValue}
        </Typography>
      </Stack>

      <Box
        sx={{
          height: 8,

          overflow: "hidden",

          borderRadius: 99,

          backgroundColor:
            "rgba(255,255,255,0.12)",
        }}
      >
        <Box
          sx={{
            width:
              `${percent}%`,

            minWidth:
              numericValue > 0
                ? 8
                : 0,

            height: "100%",

            borderRadius: 99,

            background:
              `linear-gradient(90deg, ${accent}, ${accent}CC)`,

            boxShadow:
              `0 0 18px ${accent}66`,

            transition:
              "width 500ms ease",
          }}
        />
      </Box>
    </Box>
  );
}

function OccupancyRing({
  percent,
  label,
}) {
  const safePercent =
    clampDashboardPercent(
      percent
    );

  return (
    <Box
      sx={{
        width: {
          xs: 138,
          sm: 164,
        },

        aspectRatio:
          "1 / 1",

        padding: "12px",

        borderRadius:
          "50%",

        display: "grid",
        placeItems: "center",

        background:
          `conic-gradient(
            ${COLORS.orange}
            0
            ${safePercent}%,
            rgba(255,255,255,0.12)
            ${safePercent}%
            100%
          )`,

        boxShadow:
          "0 22px 50px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(255,255,255,0.08)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",

          borderRadius:
            "50%",

          display: "grid",
          placeItems: "center",

          textAlign: "center",

          background:
            "linear-gradient(145deg, rgba(7,54,94,0.98), rgba(11,79,138,0.98))",

          border:
            "1px solid rgba(255,255,255,0.10)",

          boxShadow:
            "inset 0 12px 28px rgba(255,255,255,0.06)",
        }}
      >
        <Box>
          <Typography
            sx={{
              color:
                "rgba(255,255,255,0.68)",

              fontSize: 11,
              fontWeight: 900,

              letterSpacing: 1,

              textTransform:
                "uppercase",
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              marginTop: 0.35,

              color: "#FFFFFF",

              fontSize: {
                xs: 30,
                sm: 38,
              },

              fontWeight: 1000,
              lineHeight: 1,

              fontVariantNumeric:
                "tabular-nums",
            }}
          >
            {safePercent.toFixed(
              1
            )}
            %
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function KpiCard({
  icon,
  label,
  value,
  accent = COLORS.blue,
  sub,
}) {
  return (
    <Card
      sx={{
        ...cardStyle,

        height: "100%",

        minHeight: {
          xs: 132,
          sm: 142,
        },

        position: "relative",

        overflow: "hidden",

        background:
          `linear-gradient(
            155deg,
            #FFFFFF 0%,
            #FFFFFF 62%,
            ${accent}0D 100%
          )`,

        transition:
          "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",

        "&::before": {
          content: '""',

          position:
            "absolute",

          inset:
            "0 0 auto 0",

          height: 5,

          background:
            `linear-gradient(
              90deg,
              ${accent},
              ${COLORS.orange}
            )`,
        },

        "&::after": {
          content: '""',

          position:
            "absolute",

          width: 92,
          height: 92,

          right: -34,
          bottom: -42,

          borderRadius:
            "50%",

          backgroundColor:
            `${accent}0A`,

          pointerEvents:
            "none",
        },

        "&:hover": {
          transform:
            "translateY(-4px)",

          borderColor:
            `${accent}44`,

          boxShadow:
            "0 22px 48px rgba(15, 23, 42, 0.12)",
        },
      }}
    >
      <CardContent
        sx={{
          height: "100%",

          padding: {
            xs: 1.7,
            sm: 2.1,
          },

          "&:last-child": {
            paddingBottom: {
              xs: 1.7,
              sm: 2.1,
            },
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1.25}
          sx={{
            height: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              minWidth: 0,

              flex:
                "1 1 auto",

              paddingTop: 0.35,
            }}
          >
            <Typography
              sx={{
                minHeight: 34,

                color:
                  COLORS.muted,

                fontSize: {
                  xs: 11.5,
                  sm: 12.5,
                },

                fontWeight: 900,
                lineHeight: 1.35,

                letterSpacing: 0.15,

                overflowWrap:
                  "anywhere",
              }}
            >
              {label}
            </Typography>

            <Typography
              title={String(
                value
              )}
              sx={{
                width: "100%",
                maxWidth: "100%",

                marginTop: 0.7,

                overflow:
                  "hidden",

                color:
                  COLORS.text,

                fontSize: {
                  xs: "clamp(1.25rem, 5vw, 1.65rem)",
                  sm: "1.8rem",
                  lg: "2rem",
                },

                fontWeight: 1000,
                lineHeight: 1.05,

                letterSpacing: -0.8,

                whiteSpace:
                  "nowrap",

                textOverflow:
                  "ellipsis",

                fontVariantNumeric:
                  "tabular-nums",
              }}
            >
              {value}
            </Typography>

            {sub && (
              <Typography
                sx={{
                  marginTop: 0.7,

                  color:
                    COLORS.muted,

                  fontSize: 11.5,
                  fontWeight: 700,
                }}
              >
                {sub}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              width: {
                xs: 42,
                sm: 48,
              },

              height: {
                xs: 42,
                sm: 48,
              },

              flexShrink: 0,

              borderRadius: 3,

              display: "grid",
              placeItems: "center",

              background:
                `linear-gradient(
                  145deg,
                  ${accent}20,
                  ${accent}0D
                )`,

              border:
                `1px solid ${accent}20`,

              boxShadow:
                `0 10px 24px ${accent}18`,

              fontSize: {
                xs: 21,
                sm: 24,
              },
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function EmptyReservation() {
  return {
    id: null,

    guest_id: "",

    room_type_id: "",
    room_id: "",

    check_in:
      isoDate(),

    check_out:
      addDays(
        isoDate(),
        1
      ),

    adults: 2,
    children: 0,
    infants: 0,

    source: "direct",

    nightly_rate: "",

    currency: "TRY",

    special_requests: "",
    internal_notes: "",

    status: "confirmed",
  };
}

function EmptyRoom() {
  return {
    id: null,

    room_no: "",
    room_type_id: "",

    floor_name: "",
    building_name: "",

    status:
      "vacant_clean",

    housekeeping_status:
      "clean",

    phone_extension: "",

    notes: "",
  };
}

function EmptyRoomType() {
  return {
    id: null,

    code: "",

    name_tr: "",
    name_en: "",
    name_de: "",

    description_tr: "",
    description_en: "",
    description_de: "",

    base_price: "",

    currency: "TRY",

    max_occupancy: 2,

    bed_type: "",
    size_m2: "",
  };
}

function EmptyGuest() {
  return {
    id: null,

    first_name: "",
    last_name: "",

    phone: "",
    email: "",

    country_code: "TR",
    nationality: "",

    identity_type:
      "passport",

    identity_no: "",
    passport_no: "",

    birth_date: "",

    preferred_language:
      "tr",

    vip_level:
      "standard",

    preferences: "",

    allergy_info: "",

    special_requests: "",

    notes: "",
  };
}

function EmptyHousekeepingTask() {
  return {
    room_id: "",

    task_date:
      isoDate(),

    task_type:
      "stayover_cleaning",

    priority:
      "normal",

    assigned_name: "",

    notes: "",
  };
}

function EmptyMaintenanceIssue() {
  return {
    room_id: "",

    title: "",

    category:
      "general",

    priority:
      "normal",

    assigned_name: "",

    description: "",

    estimated_cost: "",

    room_blocked:
      false,
  };
}

export default function HotelYonetimiPage() {
const mobile =
  useMediaQuery(
    "(max-width:930px)"
  );

  const initialLanguage =
    localStorage.getItem(
      "panel_lang"
    ) ||
    localStorage.getItem(
      "lang"
    ) ||
    "tr";

  const [language, setLanguage] =
    useState(
      LANGUAGES.includes(
        initialLanguage
      )
        ? initialLanguage
        : "tr"
    );

  const translate =
    useCallback(
      (key) =>
        TRANSLATIONS[
          language
        ]?.[key] ||
        TRANSLATIONS.tr[key] ||
        key,

      [language]
    );

  const [tab, setTab] =
    useState(0);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    notification,
    setNotification,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [
    property,
    setProperty,
  ] = useState({});

  const [
    dashboard,
    setDashboard,
  ] = useState({});

  const [
    roomTypes,
    setRoomTypes,
  ] = useState([]);

  const [
    rooms,
    setRooms,
  ] = useState([]);

  const [
    guests,
    setGuests,
  ] = useState([]);

  const [
    reservations,
    setReservations,
  ] = useState([]);

  const [
    calendar,
    setCalendar,
  ] = useState({
    rooms: [],
    reservations: [],
  });

  const [
    housekeeping,
    setHousekeeping,
  ] = useState([]);

  const [
    maintenance,
    setMaintenance,
  ] = useState([]);

  const [
    reportData,
    setReportData,
  ] = useState({
    summary: {},
    countries: [],
    room_types: [],
    sources: [],
  });

  const [
    searchText,
    setSearchText,
  ] = useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("");

  const [
    reportFrom,
    setReportFrom,
  ] = useState(
    `${isoDate().slice(0, 8)}01`
  );

  const [
    reportTo,
    setReportTo,
  ] = useState(
    isoDate()
  );

  const [
    reservationDialog,
    setReservationDialog,
  ] = useState(false);

  const [
    reservationForm,
    setReservationForm,
  ] = useState(
    EmptyReservation()
  );

  const [
    guestInviteDialog,
    setGuestInviteDialog,
  ] = useState(false);

  const [
    guestInviteReservation,
    setGuestInviteReservation,
  ] = useState(null);

  const [
    guestInvite,
    setGuestInvite,
  ] = useState(null);

  const [
    guestInviteLoading,
    setGuestInviteLoading,
  ] = useState(false);

  const [
    roomDialog,
    setRoomDialog,
  ] = useState(false);

  const [
    roomForm,
    setRoomForm,
  ] = useState(
    EmptyRoom()
  );

  const [
    roomTypeDialog,
    setRoomTypeDialog,
  ] = useState(false);

  const [
    roomTypeForm,
    setRoomTypeForm,
  ] = useState(
    EmptyRoomType()
  );

  const [
    guestDialog,
    setGuestDialog,
  ] = useState(false);

  const [
    guestForm,
    setGuestForm,
  ] = useState(
    EmptyGuest()
  );

  const [
    housekeepingDialog,
    setHousekeepingDialog,
  ] = useState(false);

  const [
    housekeepingForm,
    setHousekeepingForm,
  ] = useState(
    EmptyHousekeepingTask()
  );

  const [
    maintenanceDialog,
    setMaintenanceDialog,
  ] = useState(false);

  const [
    maintenanceForm,
    setMaintenanceForm,
  ] = useState(
    EmptyMaintenanceIssue()
  );

  const [
    folioDialog,
    setFolioDialog,
  ] = useState(false);

  const [
    folioReservation,
    setFolioReservation,
  ] = useState(null);

  const [
    folio,
    setFolio,
  ] = useState({
    reservation: null,
    folios: [],
    items: [],
    payments: [],
  });

const [
  hotelStockItems,
  setHotelStockItems,
] = useState([]);


  const [
    folioMode,
    setFolioMode,
  ] = useState("charge");

const [
  folioForm,
  setFolioForm,
] = useState({
  item_type:
    "minibar",

  stock_item_id:
    "",

  quantity:
    "1",

  description:
    "",

  amount:
    "",

  payment_method:
    "cash",
});

  const showNotification =
    useCallback(
      (
        message,
        severity = "success"
      ) => {
        setNotification({
          open: true,
          severity,
          message,
        });
      },
      []
    );

  const apiRequest =
    useCallback(
      async (
        path,
        options = {}
      ) => {
        const response =
          await fetch(
            `${API_BASE}/hotel${path}`,
            {
              ...options,

              headers: {
                ...tokenHeaders(),

                ...(
                  options.headers ||
                  {}
                ),
              },
            }
          );

        const data =
          await response
            .json()
            .catch(() => ({}));

        if (
          !response.ok ||
          data.ok === false
        ) {
          throw new Error(
            data.message ||
            data.error ||
            translate("error")
          );
        }

        return data;
      },

      [translate]
    );

  const postData =
    useCallback(
      (
        path,
        body,
        method = "POST"
      ) =>
        apiRequest(
          path,
          {
            method,

            body:
              JSON.stringify(body),
          }
        ),

      [apiRequest]
    );

  const loadReport =
    useCallback(
      async (
        from = reportFrom,
        to = reportTo
      ) => {
        const response =
          await apiRequest(
            `/reports/summary?from=${encodeURIComponent(
              from
            )}&to=${encodeURIComponent(
              to
            )}`
          );

        setReportData(
          response.data || {
            summary: {},
            countries: [],
            room_types: [],
            sources: [],
          }
        );
      },

      [
        apiRequest,
        reportFrom,
        reportTo,
      ]
    );

  const loadAll =
    useCallback(
      async () => {
        setLoading(true);

        try {
          const from =
            isoDate();

          const to =
            addDays(
              from,
              14
            );

          const [
            propertyResponse,
            dashboardResponse,
            roomTypesResponse,
            roomsResponse,
            guestsResponse,
            reservationsResponse,
            calendarResponse,
            housekeepingResponse,
            maintenanceResponse,
            reportsResponse,
          ] = await Promise.all([
            apiRequest(
              "/property"
            ),

            apiRequest(
              "/dashboard"
            ),

            apiRequest(
              "/room-types"
            ),

            apiRequest(
              "/rooms"
            ),

            apiRequest(
              "/guests"
            ),

            apiRequest(
              "/reservations"
            ),

            apiRequest(
              `/calendar?from=${from}&to=${to}`
            ),

            apiRequest(
              "/housekeeping"
            ),

            apiRequest(
              "/maintenance"
            ),

            apiRequest(
              `/reports/summary?from=${reportFrom}&to=${reportTo}`
            ),
          ]);

          setProperty(
            propertyResponse.data ||
            {}
          );

          setDashboard(
            dashboardResponse.data ||
            {}
          );

          setRoomTypes(
            roomTypesResponse.data ||
            []
          );

          setRooms(
            roomsResponse.data ||
            []
          );

          setGuests(
            guestsResponse.data ||
            []
          );

          setReservations(
            reservationsResponse.data ||
            []
          );

          setCalendar(
            calendarResponse.data || {
              rooms: [],
              reservations: [],
            }
          );

          setHousekeeping(
            housekeepingResponse.data ||
            []
          );

          setMaintenance(
            maintenanceResponse.data ||
            []
          );

          setReportData(
            reportsResponse.data || {
              summary: {},
              countries: [],
              room_types: [],
              sources: [],
            }
          );
        } catch (error) {


          showNotification(
            error.message,
            "error"
          );
        } finally {
          setLoading(false);
        }
      },

      [
        apiRequest,
        reportFrom,
        reportTo,
        showNotification,
      ]
    );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filteredReservations =
    useMemo(
      () =>
        reservations.filter(
          (reservation) => {
            const text =
              [
                reservation.reservation_no,
                reservation.guest_name,
                reservation.guest_phone,
                reservation.room_no,
                reservation.room_type_name_tr,
                reservation.room_type_name_en,
                reservation.room_type_name_de,
              ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            const searchMatches =
              !searchText ||
              text.includes(
                searchText.toLowerCase()
              );

            const statusMatches =
              !statusFilter ||
              reservation.status ===
                statusFilter;

            return (
              searchMatches &&
              statusMatches
            );
          }
        ),

      [
        reservations,
        searchText,
        statusFilter,
      ]
    );

  const filteredGuests =
    useMemo(
      () =>
        guests.filter(
          (guest) => {
            const text =
              [
                guest.first_name,
                guest.last_name,
                guest.phone,
                guest.email,
                guest.country_code,
                guest.nationality,
              ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return (
              !searchText ||
              text.includes(
                searchText.toLowerCase()
              )
            );
          }
        ),

      [
        guests,
        searchText,
      ]
    );

  const dashboardSummary =
    dashboard.summary ||
    {};

  const recentReservations =
    dashboard.recent_reservations ||
    [];

  const reportSummary =
    reportData.summary ||
    {};

  const roomTypeName =
    useCallback(
      (roomType) =>
        roomType[
          `name_${language}`
        ] ||
        roomType.name_tr ||
        roomType.name_en ||
        roomType.name_de ||
        "-",

      [language]
    );

  const reservationRoomTypeName =
    useCallback(
      (reservation) =>
        reservation[
          `room_type_name_${language}`
        ] ||
        reservation
          .room_type_name_tr ||
        reservation
          .room_type_name_en ||
        reservation
          .room_type_name_de ||
        "-",

      [language]
    );

  function changeLanguage(
    newLanguage
  ) {
    setLanguage(
      newLanguage
    );

    localStorage.setItem(
      "panel_lang",
      newLanguage
    );
  }

  function openNewReservation() {
    setReservationForm(
      EmptyReservation()
    );

    setReservationDialog(
      true
    );
  }

  function openEditReservation(
    reservation
  ) {
    setReservationForm({
      ...EmptyReservation(),

      ...reservation,

      guest_id:
        reservation.guest_id ||
        "",

      room_type_id:
        reservation.room_type_id ||
        "",

      room_id:
        reservation.room_id ||
        "",

      check_in:
        normalizeDate(
          reservation.check_in
        ),

      check_out:
        normalizeDate(
          reservation.check_out
        ),

      nightly_rate:
        reservation.nightly_rate ??
        "",
    });

    setReservationDialog(
      true
    );
  }

  function canCreateGuestInvite(
    reservation
  ) {
    return Boolean(
      reservation?.id &&
      ![
        "cancelled",
        "no_show",
        "checked_out",
      ].includes(
        reservation.status
      )
    );
  }

  function openGuestInvite(
    reservation
  ) {
    if (
      !canCreateGuestInvite(
        reservation
      )
    ) {
      showNotification(
        translate(
          "guestInviteNotAllowed"
        ),
        "warning"
      );
      return;
    }

    setGuestInviteReservation(
      reservation
    );
    setGuestInvite(null);
    setGuestInviteLoading(false);
    setGuestInviteDialog(true);
  }

  function closeGuestInvite() {
    if (guestInviteLoading) {
      return;
    }

    setGuestInviteDialog(false);
    setGuestInvite(null);
    setGuestInviteReservation(null);
  }

  async function createGuestInvite() {
    if (
      guestInviteLoading ||
      !canCreateGuestInvite(
        guestInviteReservation
      )
    ) {
      return;
    }

    setGuestInviteLoading(true);

    try {
      const response = await postData(
        `/guest-access/${guestInviteReservation.id}/invite`,
        {}
      );

      const inviteData =
        response.data || {};

      if (!inviteData.invite_token) {
        throw new Error(
          response.message ||
          translate("error")
        );
      }

      setGuestInvite(inviteData);
      showNotification(
        translate(
          "guestInviteCreated"
        )
      );
    } catch (error) {
      showNotification(
        error.message ||
          translate("error"),
        "error"
      );
    } finally {
      setGuestInviteLoading(false);
    }
  }

  async function copyGuestInviteValue(
    value
  ) {
    try {
      const copied =
        await copyTextToClipboard(
          value
        );

      if (!copied) {
        throw new Error(
          translate("error")
        );
      }

      showNotification(
        translate(
          "guestInviteCopied"
        )
      );
    } catch (error) {
      showNotification(
        error.message ||
          translate("error"),
        "error"
      );
    }
  }

  function guestInviteMessage() {
    if (!guestInvite) {
      return "";
    }

    return [
      guestInvite.property_name ||
        property.name ||
        translate("title"),
      "",
      translate(
        "guestInviteShareIntro"
      ),
      `${translate("guestInviteGuest")}: ${guestInvite.guest_name || guestInviteReservation?.guest_name || "-"}`,
      `${translate("guestInviteReservation")}: ${guestInvite.reservation_no || guestInviteReservation?.reservation_no || "-"}`,
      `${translate("guestInviteCode")}: ${guestInvite.invite_token}`,
      guestInvite.deep_link
        ? `${translate("guestInviteLink")}: ${guestInvite.deep_link}`
        : "",
      `${translate("guestInviteExpires")}: ${formatDateTime(guestInvite.expires_at, language)}`,
    ]
      .filter(Boolean)
      .join("\n");
  }

  function sendGuestInviteWhatsApp() {
    if (!guestInvite) {
      return;
    }

    const phone =
      normalizeWhatsAppPhone(
        guestInviteReservation
          ?.guest_phone
      );

    const target = phone
      ? `https://wa.me/${phone}`
      : "https://wa.me/";

    const url =
      `${target}?text=${encodeURIComponent(
        guestInviteMessage()
      )}`;

    const popup = window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );

    if (popup) {
      popup.opener = null;
    }
  }

  async function saveReservation() {
    try {
      const payload = {
        ...reservationForm,

        guest_id:
          Number(
            reservationForm.guest_id
          ),

        room_type_id:
          Number(
            reservationForm.room_type_id
          ),

        room_id:
          reservationForm.room_id
            ? Number(
                reservationForm.room_id
              )
            : null,

        adults:
          Math.max(
            1,
            Number(
              reservationForm.adults ||
              1
            )
          ),

        children:
          Math.max(
            0,
            Number(
              reservationForm.children ||
              0
            )
          ),

        infants:
          Math.max(
            0,
            Number(
              reservationForm.infants ||
              0
            )
          ),
      };

      if (
        !payload.guest_id ||
        !payload.room_type_id ||
        !payload.check_in ||
        !payload.check_out
      ) {
        throw new Error(
          translate(
            "reservationRequired"
          )
        );
      }

      await postData(
        payload.id
          ? `/reservations/${payload.id}`
          : "/reservations",

        payload,

        payload.id
          ? "PUT"
          : "POST"
      );

      setReservationDialog(
        false
      );

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

  async function reservationAction(
    reservation,
    action
  ) {
    try {
      if (
        action === "cancel" &&
        !window.confirm(
          translate(
            "confirmCancel"
          )
        )
      ) {
        return;
      }

      if (
        action === "check-out" &&
        !window.confirm(
          translate(
            "confirmCheckOut"
          )
        )
      ) {
        return;
      }

      let body = {};

      if (
        action === "cancel"
      ) {
        body = {
          reason:
            "Panel üzerinden iptal edildi",
        };
      }

      if (
        action ===
        "check-out"
      ) {
        body = {
          allow_unpaid:
            false,
        };
      }

      await postData(
        `/reservations/${reservation.id}/${action}`,
        body
      );

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

  function openNewRoom() {
    setRoomForm(
      EmptyRoom()
    );

    setRoomDialog(
      true
    );
  }

  function openEditRoom(
    room
  ) {
    setRoomForm({
      ...EmptyRoom(),
      ...room,
    });

    setRoomDialog(
      true
    );
  }

  async function saveRoom() {
    try {
      const payload = {
        ...roomForm,

        room_type_id:
          Number(
            roomForm.room_type_id
          ),
      };

      if (
        !payload.room_no ||
        !payload.room_type_id
      ) {
        throw new Error(
          translate("error")
        );
      }

      await postData(
        payload.id
          ? `/rooms/${payload.id}`
          : "/rooms",

        payload,

        payload.id
          ? "PUT"
          : "POST"
      );

      setRoomDialog(false);

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

  function openNewRoomType() {
    setRoomTypeForm(
      EmptyRoomType()
    );

    setRoomTypeDialog(
      true
    );
  }

  function openEditRoomType(
    roomType
  ) {
    setRoomTypeForm({
      ...EmptyRoomType(),
      ...roomType,
    });

    setRoomTypeDialog(
      true
    );
  }

  async function saveRoomType() {
    try {
      const payload = {
        ...roomTypeForm,

        base_price:
          Number(
            roomTypeForm.base_price ||
            0
          ),

        max_occupancy:
          Math.max(
            1,
            Number(
              roomTypeForm.max_occupancy ||
              1
            )
          ),
      };

      if (
        !payload.name_tr
      ) {
        throw new Error(
          translate("nameTr")
        );
      }

      await postData(
        payload.id
          ? `/room-types/${payload.id}`
          : "/room-types",

        payload,

        payload.id
          ? "PUT"
          : "POST"
      );

      setRoomTypeDialog(
        false
      );

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

  async function updateRoomStatus(
    room,
    status
  ) {
    try {
      const housekeepingStatus =
        status ===
          "vacant_dirty"
          ? "dirty"
          : status ===
              "vacant_clean"
          ? "clean"
          : status ===
              "inspected"
          ? "inspected"
          : undefined;

      await postData(
        `/rooms/${room.id}/status`,

        {
          status,

          ...(housekeepingStatus
            ? {
                housekeeping_status:
                  housekeepingStatus,
              }
            : {}),
        },

        "PUT"
      );

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

  function openNewGuest() {
    setGuestForm(
      EmptyGuest()
    );

    setGuestDialog(
      true
    );
  }

  function openEditGuest(
    guest
  ) {
    setGuestForm({
      ...EmptyGuest(),

      ...guest,

      identity_no: "",
      passport_no: "",

      birth_date:
        normalizeDate(
          guest.birth_date
        ),

      preferences:
        guest.preferences_json
          ?.text ||
        "",

      allergy_info:
        guest.allergy_info ||
        "",
    });

    setGuestDialog(
      true
    );
  }

  async function saveGuest() {
    try {
      const payload = {
        ...guestForm,

        preferences_json: {
          text:
            guestForm.preferences ||
            "",
        },

        allergy_info:
          guestForm.allergy_info ||
          "",
      };

      if (
        !payload.first_name
      ) {
        throw new Error(
          translate("firstName")
        );
      }

      await postData(
        payload.id
          ? `/guests/${payload.id}`
          : "/guests",

        payload,

        payload.id
          ? "PUT"
          : "POST"
      );

      setGuestDialog(
        false
      );

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

  function openNewHousekeepingTask() {
    setHousekeepingForm(
      EmptyHousekeepingTask()
    );

    setHousekeepingDialog(
      true
    );
  }

  async function saveHousekeepingTask() {
    try {
      const payload = {
        ...housekeepingForm,

        room_id:
          Number(
            housekeepingForm.room_id
          ),
      };

      if (!payload.room_id) {
        throw new Error(
          translate("roomNo")
        );
      }

      await postData(
        "/housekeeping",
        payload
      );

      setHousekeepingDialog(
        false
      );

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

  async function updateHousekeepingTask(
    task,
    status
  ) {
    try {
      await postData(
        `/housekeeping/${task.id}`,

        {
          status,
        },

        "PUT"
      );

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

  function openNewMaintenanceIssue() {
    setMaintenanceForm(
      EmptyMaintenanceIssue()
    );

    setMaintenanceDialog(
      true
    );
  }

  async function saveMaintenanceIssue() {
    try {
      const payload = {
        ...maintenanceForm,

        room_id:
          maintenanceForm.room_id
            ? Number(
                maintenanceForm.room_id
              )
            : null,

        estimated_cost:
          Number(
            maintenanceForm.estimated_cost ||
            0
          ),
      };

      if (!payload.title) {
        throw new Error(
          translate("titleField")
        );
      }

      await postData(
        "/maintenance",
        payload
      );

      setMaintenanceDialog(
        false
      );

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

  async function updateMaintenanceIssue(
    issue,
    status
  ) {
    try {
      await postData(
        `/maintenance/${issue.id}`,

        {
          status,

          actual_cost:
            issue.actual_cost ||
            0,
        },

        "PUT"
      );

      showNotification(
        translate("saved")
      );

      await loadAll();
    } catch (error) {
      showNotification(
        error.message,
        "error"
      );
    }
  }

async function openFolio(
  reservation
) {
  try {
    const response =
      await apiRequest(
        `/folios/${reservation.id}`
      );

    const data =
      response.data ||
      {};

    setFolioReservation(
      data.reservation ||
      reservation
    );

    setFolio(data);

    try {
      const stockResponse =
        await fetch(
          `${STOCK_API_BASE}/stock/items`,
          {
            headers:
              tokenHeaders(),
          }
        );

      const stockJson =
        await stockResponse
          .json()
          .catch(() => ({}));

      if (
        stockResponse.ok &&
        stockJson.ok !== false
      ) {
        setHotelStockItems(
          (
            stockJson.data ||
            []
          ).filter(
            (item) =>
              item.active !==
              false
          )
        );
      } else {
        setHotelStockItems(
          []
        );
      }
    } catch (
      stockError
    ) {
      console.error(
        "Otel stok listesi alınamadı:",
        stockError
      );

      setHotelStockItems(
        []
      );
    }

    setFolioForm({
      item_type:
        "minibar",

      stock_item_id:
        "",

      quantity:
        "1",

      description:
        "",

      amount:
        "",

      payment_method:
        "cash",
    });

    setFolioMode(
      "charge"
    );

    setFolioDialog(
      true
    );
  } catch (error) {
    showNotification(
      error.message,
      "error"
    );
  }
}

  async function reloadFolio() {
    if (
      !folioReservation?.id
    ) {
      return;
    }

    const response =
      await apiRequest(
        `/folios/${folioReservation.id}`
      );

    setFolio(
      response.data ||
      {}
    );
  }



async function saveFolioEntry() {
  try {
    const amount =
      Number(
        folioForm.amount ||
        0
      );

    if (amount <= 0) {
      throw new Error(
        translate("amount")
      );
    }

    if (
      folioMode ===
      "charge"
    ) {
      const quantity =
        Math.max(
          0.001,
          Number(
            folioForm.quantity ||
            1
          )
        );

      const stockItemId =
        folioForm.stock_item_id
          ? Number(
              folioForm.stock_item_id
            )
          : null;

      await postData(
        `/folios/${folioReservation.id}/items`,
        {
          item_type:
            folioForm.item_type ||
            "extra",

          description:
            folioForm.description ||
            translate("charge"),

          quantity,

          unit_price:
            amount,

          source_module:
            stockItemId
              ? "hotel_stock"
              : "hotel",

          source_type:
            folioForm.item_type ||
            "extra",

          source_id:
            stockItemId,

          sync_stock:
            Boolean(
              stockItemId
            ),
        }
      );
    } else {
      await postData(
        `/folios/${folioReservation.id}/payments`,
        {
          amount,

          payment_method:
            folioForm.payment_method,

          currency:
            folioReservation
              ?.currency ||
            property
              ?.default_currency ||
            "TRY",

          note:
            folioForm.description,

          sync_cash: false,
        }
      );
    }

    showNotification(
      folioMode === "charge" &&
      folioForm.stock_item_id
        ? "Harcama folyoya eklendi ve ürün stoktan düşüldü"
        : translate("saved")
    );

    setFolioForm({
      item_type:
        "minibar",

      stock_item_id:
        "",

      quantity:
        "1",

      description:
        "",

      amount:
        "",

      payment_method:
        "cash",
    });

    await reloadFolio();
    await loadAll();

  } catch (error) {
    showNotification(
      error.message,
      "error"
    );
  }
}

async function saveProperty() {
  try {
    await postData(
      "/property",
      property,
      "PUT"
    );

    showNotification(
      translate("saved")
    );

    await loadAll();
  } catch (error) {
    showNotification(
      error.message,
      "error"
    );
  }
}




  const tabs = [
    ["📊", "dashboard"],
    ["🗓️", "calendar"],
    ["🛎️", "reservations"],
    ["🛏️", "rooms"],
    ["👥", "guests"],
    ["🧹", "housekeeping"],
    ["🛠️", "maintenance"],
    ["💳", "finance"],
    ["📈", "reports"],
    ["⚙️", "settings"],
  ];



 return (
  <Box
    sx={{
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,

      minHeight: "100dvh",

      margin: 0,

      boxSizing: "border-box",

      overflowX: "hidden",

      background:
        "radial-gradient(circle at top left, rgba(37,99,235,0.10), transparent 34%), radial-gradient(circle at top right, rgba(249,115,22,0.12), transparent 30%), #F8FAFC",

      color:
        COLORS.text,

      "&, & *, & *::before, & *::after": {
        boxSizing: "border-box",
      },

      "& .MuiPaper-root, & .MuiCard-root": {
        maxWidth: "100%",
        minWidth: 0,
      },

      "& img": {
        maxWidth: "100%",
        height: "auto",
      },

      "@keyframes hotelRise": {


          from: {
            opacity: 0,

            transform:
              "translateY(14px)",
          },

          to: {
            opacity: 1,

            transform:
              "translateY(0)",
          },
        },

        "@keyframes hotelShine": {
          from: {
            transform:
              "translateX(-150%) rotate(20deg)",
          },

          to: {
            transform:
              "translateX(260%) rotate(20deg)",
          },
        },

        "@keyframes hotelPulse": {
          "0%, 100%": {
            transform:
              "scale(1)",

            opacity: 0.8,
          },

          "50%": {
            transform:
              "scale(1.08)",

            opacity: 1,
          },
        },
      }}
      >
      <Box
        sx={{
          width: "100%",
          maxWidth: "none",
          minWidth: 0,

          margin: 0,

          padding: {
            xs: "10px",
            sm: "16px",
            md: "20px",
            lg: "24px",
            xl: "28px",
          },

          boxSizing: "border-box",
        }}
      >
                {/* ====================================================
            ÜST KURUMSAL BAŞLIK
        ==================================================== */}

        <Paper
          sx={{
            borderRadius: 5,

            overflow:
              "hidden",

            color:
              "#FFFFFF",

            marginBottom: 2,

            background:
              `linear-gradient(125deg, ${COLORS.blueDark}, ${COLORS.blue} 55%, ${COLORS.orange})`,

            boxShadow:
              "0 20px 60px rgba(8, 59, 104, 0.25)",

            position:
              "relative",
          }}
        >
          <Box
            sx={{
              position:
                "absolute",

              width: 220,
              height: 220,

              borderRadius:
                "50%",

              backgroundColor:
                "rgba(255,255,255,0.08)",

              right: -70,
              top: -100,

              animation:
                "hotelPulse 5s ease-in-out infinite",
            }}
          />

          <Box
            sx={{
              position:
                "absolute",

              width: 130,
              height: 130,

              borderRadius:
                "50%",

              backgroundColor:
                "rgba(255,255,255,0.06)",

              left: "42%",
              bottom: -80,
            }}
          />

          <Box
            sx={{
              position:
                "absolute",

              inset: 0,

              overflow:
                "hidden",

              pointerEvents:
                "none",

              "&::after": {
                content: '""',

                position:
                  "absolute",

                width: 100,
                height: "220%",

                top: "-60%",

                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",

                animation:
                  "hotelShine 7s linear infinite",
              },
            }}
          />

          <Box
            sx={{
              padding: {
                xs: 2,
                md: 3,
              },

              position:
                "relative",
            }}
          >
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}

              justifyContent=
                "space-between"

              alignItems={{
                xs: "stretch",
                md: "center",
              }}

              spacing={2}
            >
              <Stack
                direction="row"

                spacing={2}

                alignItems=
                  "center"
              >
                <Box
                  sx={{
                    width: {
                      xs: 54,
                      sm: 66,
                    },

                    height: {
                      xs: 54,
                      sm: 66,
                    },

                    flexShrink: 0,

                    borderRadius: 4,

                    backgroundColor:
                      "rgba(255,255,255,0.16)",

                    display:
                      "grid",

                    placeItems:
                      "center",

                    fontSize: {
                      xs: 28,
                      sm: 36,
                    },

                    border:
                      "1px solid rgba(255,255,255,0.22)",

                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.18)",
                  }}
                >
                  🏨
                </Box>

                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant={
                      mobile
                        ? "h5"
                        : "h4"
                    }

                    fontWeight={1000}

                    sx={{
                      lineHeight: 1.1,
                    }}
                  >
                    {property.name ||
                      translate(
                        "title"
                      )}
                  </Typography>

                  <Typography
                    sx={{
                      opacity: 0.88,

                      marginTop: 0.5,

                      fontSize: {
                        xs: 13,
                        sm: 15,
                      },
                    }}
                  >
                    {translate(
                      "subtitle"
                    )}
                  </Typography>
                </Box>
              </Stack>

                            <Stack
                direction="row"

                spacing={1}

                useFlexGap

                flexWrap="wrap"

                justifyContent={{
                  xs: "stretch",
                  md: "flex-end",
                }}

                sx={{
                  width: {
                    xs: "100%",
                    md: "auto",
                  },

                  minWidth: 0,

                  "& .MuiButton-root": {
                    minHeight: 42,

                    flex: {
                      xs: "1 1 72px",
                      sm: "0 0 auto",
                    },

                    whiteSpace: "nowrap",
                  },
                }}
              >
                {LANGUAGES.map(

                  (item) => (
                    <Button
                      key={item}

                      onClick={() =>
                        changeLanguage(
                          item
                        )
                      }

                      sx={{
                        minWidth: 46,

                        borderRadius:
                          99,

                        color:
                          "#FFFFFF",

                        fontWeight:
                          1000,

                        backgroundColor:
                          language ===
                          item
                            ? "rgba(255,255,255,0.26)"
                            : "rgba(255,255,255,0.10)",

                        border:
                          "1px solid rgba(255,255,255,0.18)",

                        "&:hover": {
                          backgroundColor:
                            "rgba(255,255,255,0.24)",
                        },
                      }}
                    >
                      {item.toUpperCase()}
                    </Button>
                  )
                )}

                <Button
                  onClick={
                    loadAll
                  }

                  sx={{
                    borderRadius:
                      99,

                    color:
                      "#FFFFFF",

                    backgroundColor:
                      "rgba(255,255,255,0.14)",

                    fontWeight:
                      1000,

                    border:
                      "1px solid rgba(255,255,255,0.18)",

                    "&:hover": {
                      backgroundColor:
                        "rgba(255,255,255,0.24)",
                    },
                  }}
                >
                  ↻{" "}
                  {translate(
                    "refresh"
                  )}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>



{/* ====================================================
    PROFESYONEL OTEL MENÜSÜ
==================================================== */}

<Paper
  sx={{
    ...cardStyle,

    width: "100%",
    maxWidth: "100%",
    minWidth: 0,

    padding: {
      xs: 0.85,
      sm: 1,
    },

    marginBottom: 2,

    overflow: "hidden",

    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
  }}
>
  <Box
    sx={{
      display: "grid",

      gridTemplateColumns:
        "repeat(10, minmax(0, 1fr))",

      gap: 0.8,

      width: "100%",
      maxWidth: "100%",
      minWidth: 0,

      "@media (max-width:1500px)": {
        gridTemplateColumns:
          "repeat(5, minmax(0, 1fr))",
      },

      "@media (max-width:930px)": {
        gridTemplateColumns:
          "repeat(2, minmax(0, 1fr))",

        gap: 0.75,
      },
    }}
  >
    {tabs.map(
      (
        [icon, key],
        index
      ) => {
        const active =
          tab === index;

        return (
          <Button
            key={key}
            type="button"

            aria-pressed={
              active
            }

            onClick={() => {
              setTab(index);

              setSearchText(
                ""
              );

              setStatusFilter(
                ""
              );
            }}

            sx={{
              width: "100%",
              minWidth: 0,

              minHeight: {
                xs: 66,
                sm: 72,
              },

              position:
                "relative",

              overflow:
                "hidden",

              justifyContent:
                "flex-start",

              gap: {
                xs: 0.9,
                sm: 1,
              },

              padding: {
                xs: "9px 10px",
                sm: "10px 12px",
              },

              borderRadius: 3,

              border: active
                ? `1px solid ${COLORS.blue}`
                : `1px solid ${COLORS.border}`,

              color: active
                ? "#FFFFFF"
                : COLORS.text,

              background: active
                ? `linear-gradient(
                    135deg,
                    ${COLORS.blueDark},
                    ${COLORS.blue}
                  )`
                : "#FFFFFF",

              boxShadow: active
                ? "0 12px 28px rgba(11,79,138,0.24)"
                : "0 4px 14px rgba(15,23,42,0.04)",

              textTransform:
                "none",

              transition:
                "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",

              "&::after": active
                ? {
                    content:
                      '""',

                    position:
                      "absolute",

                    left: 12,
                    right: 12,
                    bottom: 0,

                    height: 4,

                    borderRadius:
                      "99px 99px 0 0",

                    backgroundColor:
                      COLORS.orange,
                  }
                : undefined,

              "&:hover": {
                transform:
                  "translateY(-2px)",

                borderColor:
                  active
                    ? COLORS.blue
                    : `${COLORS.blue}55`,

                background:
                  active
                    ? `linear-gradient(
                        135deg,
                        ${COLORS.blueDark},
                        ${COLORS.blue}
                      )`
                    : COLORS.blueLight,

                boxShadow:
                  active
                    ? "0 15px 32px rgba(11,79,138,0.28)"
                    : "0 10px 24px rgba(15,23,42,0.08)",
              },
            }}
          >
            <Box
              sx={{
                width: {
                  xs: 34,
                  sm: 38,
                },

                height: {
                  xs: 34,
                  sm: 38,
                },

                flexShrink: 0,

                borderRadius:
                  2.5,

                display:
                  "grid",

                placeItems:
                  "center",

                backgroundColor:
                  active
                    ? "rgba(255,255,255,0.14)"
                    : COLORS.blueLight,

                border:
                  active
                    ? "1px solid rgba(255,255,255,0.16)"
                    : `1px solid ${COLORS.blue}14`,

                fontSize: {
                  xs: 17,
                  sm: 19,
                },
              }}
            >
              {icon}
            </Box>

            <Typography
              component="span"

              sx={{
                minWidth: 0,

                color:
                  "inherit",

                fontSize: {
                  xs: 11.5,
                  sm: 12.5,
                },

                fontWeight:
                  1000,

                lineHeight:
                  1.25,

                textAlign:
                  "left",

                whiteSpace:
                  "normal",

                overflowWrap:
                  "anywhere",

                display:
                  "-webkit-box",

                WebkitBoxOrient:
                  "vertical",

                WebkitLineClamp:
                  2,

                overflow:
                  "hidden",
              }}
            >
              {translate(
                key
              )}
            </Typography>
          </Button>
        );
      }
    )}
  </Box>
</Paper>

{loading ? (
  <Box
    sx={{
      minHeight: 320,
      display: "grid",
      placeItems: "center",
    }}
  >
    <Stack
      alignItems="center"
      spacing={1.5}
    >
      <CircularProgress
        size={42}
        thickness={4}
        sx={{
          color: COLORS.blue,
        }}
      />

      <Typography
        sx={{
          color: COLORS.muted,
          fontWeight: 900,
        }}
      >
        {translate("loading")}
      </Typography>
    </Stack>
  </Box>
) : (
  <Box
    sx={{
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
    }}
  >




            {/* ====================================================
                GENEL BAKIŞ
            ==================================================== */}

          {tab === 0 && (
  <>
    {/* ÜST KPI KARTLARI */}

    <ResponsiveGrid
      container
      spacing={2}
    >

{folioMode ===
  "charge" && (
  <>
    <ResponsiveGrid
      xs={12}
      md={3}
    >
      <TextField
        select
        fullWidth

        label="İşlem Türü"

        value={
          folioForm.item_type
        }

        onChange={(
          event
        ) =>
          setFolioForm(
            (
              previous
            ) => ({
              ...previous,

              item_type:
                event.target.value,
            })
          )
        }

        sx={
          inputStyle
        }
      >
        <MenuItem
          value="minibar"
        >
          🥤 Minibar
        </MenuItem>

        <MenuItem
          value="room_service"
        >
          🍽️ Oda Servisi
        </MenuItem>

        <MenuItem
          value="restaurant"
        >
          🧾 Restoran
        </MenuItem>

        <MenuItem
          value="laundry"
        >
          👕 Çamaşırhane
        </MenuItem>

        <MenuItem
          value="extra"
        >
          ➕ Diğer Hizmet
        </MenuItem>
      </TextField>
    </ResponsiveGrid>

    <ResponsiveGrid
      xs={12}
      md={6}
    >
      <TextField
        select
        fullWidth

        label="Stok Ürünü"

        value={
          folioForm.stock_item_id
        }

        onChange={(
          event
        ) => {
          const selectedId =
            event.target.value;

          const selectedItem =
            hotelStockItems.find(
              (item) =>
                String(
                  item.id
                ) ===
                String(
                  selectedId
                )
            );

          setFolioForm(
            (
              previous
            ) => ({
              ...previous,

              stock_item_id:
                selectedId,

              description:
                selectedItem
                  ?.name ||
                previous
                  .description,

              amount:
                selectedItem
                  ? String(
                      Number(
                        selectedItem
                          .sale_price ||
                        0
                      )
                    )
                  : previous
                      .amount,
            })
          );
        }}

        sx={
          inputStyle
        }
      >
        <MenuItem
          value=""
        >
          Stok bağlantısı olmadan hizmet ekle
        </MenuItem>

        {hotelStockItems.map(
          (item) => (
            <MenuItem
              key={
                item.id
              }

              value={
                item.id
              }

              disabled={
                Number(
                  item.current_stock ||
                  0
                ) <= 0
              }
            >
              {item.name}
              {" · Stok: "}
              {Number(
                item.current_stock ||
                0
              )}
              {" · "}
              {money(
                item.sale_price ||
                  0,

                item.currency ||
                  "TRY",

                language
              )}
            </MenuItem>
          )
        )}
      </TextField>
    </ResponsiveGrid>

    <ResponsiveGrid
      xs={12}
      md={3}
    >
      <TextField
        fullWidth

        type="number"

        label="Adet / Miktar"

        value={
          folioForm.quantity
        }

        inputProps={{
          min: 0.001,
          step: 1,
        }}

        onChange={(
          event
        ) =>
          setFolioForm(
            (
              previous
            ) => ({
              ...previous,

              quantity:
                event.target.value,
            })
          )
        }

        sx={
          inputStyle
        }
      />
    </ResponsiveGrid>
  </>
)}



      {[
        {
          key:
            "totalRooms",

          icon:
            "🛏️",

          label:
            translate(
              "totalRooms"
            ),

          value:
            dashboardSummary.total_rooms ||
            0,

          accent:
            COLORS.blue,
        },

        {
          key:
            "occupied",

          icon:
            "🔑",

          label:
            translate(
              "occupied"
            ),

          value:
            dashboardSummary.occupied_rooms ||
            0,

          accent:
            COLORS.orange,
        },

        {
          key:
            "available",

          icon:
            "✅",

          label:
            translate(
              "available"
            ),

          value:
            dashboardSummary.available_rooms ||
            0,

          accent:
            COLORS.green,
        },

        {
          key:
            "dirty",

          icon:
            "🧹",

          label:
            translate(
              "dirty"
            ),

          value:
            dashboardSummary.dirty_rooms ||
            0,

          accent:
            COLORS.amber,
        },

        {
          key:
            "arrivals",

          icon:
            "📥",

          label:
            translate(
              "arrivals"
            ),

          value:
            dashboardSummary.arrivals_today ||
            0,

          accent:
            COLORS.purple,
        },

        {
          key:
            "departures",

          icon:
            "📤",

          label:
            translate(
              "departures"
            ),

          value:
            dashboardSummary.departures_today ||
            0,

          accent:
            COLORS.blue,
        },

        {
          key:
            "todayRevenue",

          icon:
            "💰",

          label:
            translate(
              "todayRevenue"
            ),

          value:
            money(
              dashboardSummary.today_revenue ||
                0,

              property.default_currency,

              language
            ),

          accent:
            COLORS.green,
        },

        {
          key:
            "openBalance",

          icon:
            "⚠️",

          label:
            translate(
              "openBalance"
            ),

          value:
            money(
              dashboardSummary.open_balance ||
                0,

              property.default_currency,

              language
            ),

          accent:
            COLORS.red,
        },
      ].map(
        (item) => (
          <ResponsiveGrid
            key={item.key}

            xs={6}
            md={6}
            lg={3}
          >
            <KpiCard
              icon={
                item.icon
              }

              label={
                item.label
              }

              value={
                item.value
              }

              accent={
                item.accent
              }
            />
          </ResponsiveGrid>
        )
      )}
    </ResponsiveGrid>

    {/* ALT PANEL */}

    <ResponsiveGrid
      container
      spacing={2}

      sx={{
        marginTop: 0.2,
      }}
    >
      {/* SON REZERVASYONLAR */}

      <ResponsiveGrid
        xs={12}
        lg={8}
      >
        <Card
          sx={{
            ...cardStyle,

            height:
              "100%",

            background:
              "linear-gradient(180deg, #FFFFFF 0%, #FBFDFF 100%)",
          }}
        >
          <CardContent>
            <SectionTitle
              title={translate(
                "recentReservations"
              )}

              action={
                <Chip
                  size="small"

                  label={`${recentReservations.length} ${translate(
                    "reservations"
                  )}`}

                  sx={{
                    fontWeight:
                      1000,

                    color:
                      COLORS.blue,

                    backgroundColor:
                      COLORS.blueLight,
                  }}
                />
              }
            />

            {recentReservations.length >
            0 ? (
              <Stack
                spacing={1}
              >
                {recentReservations
                  .slice(
                    0,
                    6
                  )
                  .map(
                    (
                      reservation
                    ) => (
                      <Stack
                        key={
                          reservation.id
                        }

                        direction={{
                          xs: "column",
                          sm: "row",
                        }}

                        justifyContent=
                          "space-between"

                        alignItems={{
                          xs: "stretch",
                          sm: "center",
                        }}

                        spacing={
                          1.25
                        }

                        sx={{
                          width:
                            "100%",

                          minWidth:
                            0,

                          padding: {
                            xs: 1.25,
                            sm: 1.5,
                          },

                          border:
                            `1px solid ${COLORS.border}`,

                          borderRadius:
                            3,

                          backgroundColor:
                            "#FFFFFF",

                          transition:
                            "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",

                          "&:hover":
                            {
                              transform:
                                "translateY(-2px)",

                              borderColor:
                                `${COLORS.blue}44`,

                              boxShadow:
                                "0 12px 28px rgba(15,23,42,0.07)",
                            },
                        }}
                      >
                        <Stack
                          direction=
                            "row"

                          spacing={
                            1.2
                          }

                          alignItems=
                            "center"

                          sx={{
                            minWidth:
                              0,

                            flex:
                              "1 1 auto",
                          }}
                        >
                          <Box
                            sx={{
                              width:
                                42,

                              height:
                                42,

                              flexShrink:
                                0,

                              borderRadius:
                                2.5,

                              display:
                                "grid",

                              placeItems:
                                "center",

                              background:
                                `linear-gradient(
                                  145deg,
                                  ${COLORS.blueLight},
                                  ${COLORS.orangeLight}
                                )`,

                              border:
                                `1px solid ${COLORS.border}`,

                              fontSize:
                                20,
                            }}
                          >
                            🧳
                          </Box>

                          <Box
                            sx={{
                              minWidth:
                                0,

                              flex:
                                "1 1 auto",
                            }}
                          >
                            <Typography
                              fontWeight={
                                1000
                              }

                              noWrap

                              sx={{
                                color:
                                  COLORS.text,
                              }}
                            >
                              {reservation.guest_name ||
                                reservation.reservation_no}
                            </Typography>

                            <Typography
                              sx={{
                                marginTop:
                                  0.3,

                                color:
                                  COLORS.muted,

                                fontSize:
                                  12.5,

                                fontWeight:
                                  700,

                                lineHeight:
                                  1.45,

                                overflowWrap:
                                  "anywhere",
                              }}
                            >
                              {
                                reservation.reservation_no
                              }

                              {
                                " · "
                              }

                              {normalizeDate(
                                reservation.check_in
                              )}

                              {
                                " → "
                              }

                              {normalizeDate(
                                reservation.check_out
                              )}

                              {
                                " · "
                              }

                              {reservation.room_no ||
                                reservation.room_type_name ||
                                translate(
                                  "roomType"
                                )}
                            </Typography>
                          </Box>
                        </Stack>

                        <Box
                          sx={{
                            flexShrink:
                              0,

                            alignSelf:
                              {
                                xs: "flex-start",
                                sm: "center",
                              },
                          }}
                        >
                          <StatusChip
                            value={
                              reservation.status
                            }

                            translate={
                              translate
                            }
                          />
                        </Box>
                      </Stack>
                    )
                  )}
              </Stack>
            ) : (
              <EmptyState
                translate={
                  translate
                }
              />
            )}
          </CardContent>
        </Card>
      </ResponsiveGrid>

      {/* PROFESYONEL DOLULUK PANELİ */}

      <ResponsiveGrid
        xs={12}
        lg={4}
      >
        <Card
          sx={{
            ...cardStyle,

            height:
              "100%",

            minHeight:
              460,

            color:
              "#FFFFFF",

            background:
              `radial-gradient(
                circle at 85% 10%,
                rgba(249,115,22,0.28),
                transparent 28%
              ),
              linear-gradient(
                145deg,
                ${COLORS.blueDark},
                ${COLORS.blue}
              )`,

            border:
              "1px solid rgba(255,255,255,0.08)",

            boxShadow:
              "0 24px 60px rgba(7,54,94,0.28)",
          }}
        >
          <CardContent
            sx={{
              height:
                "100%",

              display:
                "flex",

              flexDirection:
                "column",

              gap: 2,
            }}
          >
            <Stack
              direction=
                "row"

              justifyContent=
                "space-between"

              alignItems=
                "flex-start"

              spacing={1}
            >
              <Box>
                <Typography
                  sx={{
                    color:
                      "rgba(255,255,255,0.68)",

                    fontSize:
                      11,

                    fontWeight:
                      900,

                    letterSpacing:
                      1.1,

                    textTransform:
                      "uppercase",
                  }}
                >
                  {translate(
                    "summary"
                  )}
                </Typography>

                <Typography
                  variant="h6"

                  fontWeight={
                    1000
                  }

                  sx={{
                    marginTop:
                      0.35,
                  }}
                >
                  {translate(
                    "occupancyRate"
                  )}
                </Typography>
              </Box>

              <Chip
                size="small"

                label={`${dashboardSummary.total_rooms || 0} ${translate(
                  "totalRooms"
                )}`}

                sx={{
                  color:
                    "#FFFFFF",

                  fontWeight:
                    1000,

                  backgroundColor:
                    "rgba(255,255,255,0.12)",

                  border:
                    "1px solid rgba(255,255,255,0.12)",
                }}
              />
            </Stack>

            <Box
              sx={{
                display:
                  "grid",

                placeItems:
                  "center",

                paddingY:
                  0.5,
              }}
            >
              <OccupancyRing
                label={translate(
                  "occupancy"
                )}

                percent={
                  dashboardSummary.occupancy_rate ||
                  0
                }
              />
            </Box>

            {/* AYLIK GELİR */}

            <Box
              sx={{
                padding:
                  1.35,

                borderRadius:
                  3,

                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.07))",

                border:
                  "1px solid rgba(255,255,255,0.11)",

                backdropFilter:
                  "blur(12px)",
              }}
            >
              <Stack
                direction=
                  "row"

                justifyContent=
                  "space-between"

                alignItems=
                  "center"

                spacing={1}
              >
                <Box>
                  <Typography
                    sx={{
                      color:
                        "rgba(255,255,255,0.68)",

                      fontSize:
                        11.5,

                      fontWeight:
                        800,
                    }}
                  >
                    {translate(
                      "monthRevenue"
                    )}
                  </Typography>

                  <Typography
                    sx={{
                      marginTop:
                        0.25,

                      color:
                        "#FFFFFF",

                      fontSize:
                        {
                          xs: 20,
                          sm: 23,
                        },

                      fontWeight:
                        1000,

                      lineHeight:
                        1.15,

                      fontVariantNumeric:
                        "tabular-nums",
                    }}
                  >
                    {money(
                      dashboardSummary.month_revenue ||
                        0,

                      property.default_currency,

                      language
                    )}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width:
                      42,

                    height:
                      42,

                    flexShrink:
                      0,

                    borderRadius:
                      2.5,

                    display:
                      "grid",

                    placeItems:
                      "center",

                    backgroundColor:
                      "rgba(249,115,22,0.22)",

                    border:
                      "1px solid rgba(249,115,22,0.28)",

                    fontSize:
                      21,
                  }}
                >
                  💰
                </Box>
              </Stack>
            </Box>

            {/* ODA DAĞILIM ÇUBUKLARI */}

            <Stack
              spacing={1.35}
            >
              <DashboardProgressRow
                icon="✅"

                label={translate(
                  "available"
                )}

                value={
                  dashboardSummary.available_rooms ||
                  0
                }

                total={
                  dashboardSummary.total_rooms ||
                  0
                }

                accent={
                  COLORS.green
                }
              />

              <DashboardProgressRow
                icon="🔑"

                label={translate(
                  "occupied"
                )}

                value={
                  dashboardSummary.occupied_rooms ||
                  0
                }

                total={
                  dashboardSummary.total_rooms ||
                  0
                }

                accent={
                  COLORS.orange
                }
              />

              <DashboardProgressRow
                icon="🧹"

                label={translate(
                  "dirty"
                )}

                value={
                  dashboardSummary.dirty_rooms ||
                  0
                }

                total={
                  dashboardSummary.total_rooms ||
                  0
                }

                accent={
                  COLORS.amber
                }
              />

              <DashboardProgressRow
                icon="🛠️"

                label={translate(
                  "blockedRooms"
                )}

                value={
                  dashboardSummary.blocked_rooms ||
                  0
                }

                total={
                  dashboardSummary.total_rooms ||
                  0
                }

                accent={
                  COLORS.red
                }
              />
            </Stack>

            <Stack
              direction=
                "row"

              justifyContent=
                "space-between"

              alignItems=
                "center"

              sx={{
                marginTop:
                  "auto",

                paddingTop:
                  1.35,

                borderTop:
                  "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <Typography
                sx={{
                  color:
                    "rgba(255,255,255,0.72)",

                  fontSize:
                    12.5,

                  fontWeight:
                    800,
                }}
              >
                {translate(
                  "housekeeping"
                )}
              </Typography>

              <Typography
                sx={{
                  color:
                    "#FFFFFF",

                  fontWeight:
                    1000,

                  fontVariantNumeric:
                    "tabular-nums",
                }}
              >
                {dashboardSummary.housekeeping_pending ||
                  0}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </ResponsiveGrid>
    </ResponsiveGrid>
  </>
)}


            {/* ====================================================
                DOLULUK TAKVİMİ
            ==================================================== */}

            {tab === 1 && (
              <Card
                sx={
                  cardStyle
                }
              >
                <CardContent>
                  <SectionTitle
                    title={translate(
                      "calendar"
                    )}

                    subtitle={`${isoDate()} — ${addDays(
                      isoDate(),
                      13
                    )}`}
                  />

                  <Box
                    sx={{
                      overflowX:
                        "auto",

                      paddingBottom:
                        1,

                      WebkitOverflowScrolling:
                        "touch",
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 1120,

                        display:
                          "grid",

                        gridTemplateColumns:
                          "135px repeat(14, minmax(68px, 1fr))",

                        border:
                          `1px solid ${COLORS.border}`,

                        borderRadius:
                          3,

                        overflow:
                          "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          padding:
                            1.2,

                          backgroundColor:
                            COLORS.blueDark,

                          color:
                            "#FFFFFF",

                          fontWeight:
                            1000,
                        }}
                      >
                        {translate(
                          "roomNo"
                        )}
                      </Box>

                      {Array.from({
                        length: 14,
                      }).map(
                        (
                          _item,
                          index
                        ) => {
                          const date =
                            addDays(
                              isoDate(),
                              index
                            );

                          return (
                            <Box
                              key={
                                date
                              }

                              sx={{
                                padding:
                                  1,

                                backgroundColor:
                                  COLORS.blueDark,

                                color:
                                  "#FFFFFF",

                                borderLeft:
                                  "1px solid rgba(255,255,255,0.12)",

                                textAlign:
                                  "center",

                                fontWeight:
                                  900,

                                fontSize:
                                  12,
                              }}
                            >
                              {date.slice(
                                5
                              )}
                            </Box>
                          );
                        }
                      )}

                      {(calendar.rooms ||
                        rooms).map(
                        (room) => (
                          <React.Fragment
                            key={
                              room.id
                            }
                          >
                            <Box
                              sx={{
                                padding:
                                  1,

                                fontWeight:
                                  1000,

                                backgroundColor:
                                  "#FFFFFF",

                                borderTop:
                                  `1px solid ${COLORS.border}`,

                                position:
                                  "sticky",

                                left: 0,

                                zIndex:
                                  2,
                              }}
                            >
                              {room.room_no}

                              <Typography
                                sx={{
                                  fontSize:
                                    10,

                                  color:
                                    COLORS.muted,
                                }}
                              >
                                {room[
                                  `room_type_name_${language}`
                                ] ||
                                  room.room_type_name_tr ||
                                  ""}
                              </Typography>
                            </Box>

                            {Array.from({
                              length:
                                14,
                            }).map(
                              (
                                _day,
                                index
                              ) => {
                                const date =
                                  addDays(
                                    isoDate(),
                                    index
                                  );

                                const booking =
                                  (
                                    calendar.reservations ||
                                    []
                                  ).find(
                                    (
                                      item
                                    ) =>
                                      Number(
                                        item.room_id
                                      ) ===
                                        Number(
                                          room.id
                                        ) &&
                                      date >=
                                        normalizeDate(
                                          item.check_in
                                        ) &&
                                      date <
                                        normalizeDate(
                                          item.check_out
                                        ) &&
                                      item.status !==
                                        "cancelled"
                                  );

                                return (
                                  <Tooltip
                                    key={
                                      date
                                    }

                                    title={
                                      booking
                                        ? `${booking.guest_name || ""} · ${booking.reservation_no || ""}`
                                        : translate(
                                            "available"
                                          )
                                    }
                                  >
                                    <Box
                                      sx={{
                                        minHeight:
                                          50,

                                        borderTop:
                                          `1px solid ${COLORS.border}`,

                                        borderLeft:
                                          `1px solid ${COLORS.border}`,

                                        backgroundColor:
                                          booking
                                            ? COLORS.orangeLight
                                            : COLORS.greenLight,

                                        display:
                                          "grid",

                                        placeItems:
                                          "center",

                                        fontSize:
                                          12,

                                        fontWeight:
                                          1000,

                                        color:
                                          booking
                                            ? COLORS.orange
                                            : COLORS.green,

                                        transition:
                                          "all 0.2s ease",

                                        "&:hover":
                                          {
                                            transform:
                                              "scale(1.04)",

                                            zIndex:
                                              1,

                                            boxShadow:
                                              "0 5px 16px rgba(15,23,42,0.12)",
                                          },
                                      }}
                                    >
                                      {booking
                                        ? "●"
                                        : "✓"}
                                    </Box>
                                  </Tooltip>
                                );
                              }
                            )}
                          </React.Fragment>
                        )
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* ====================================================
                REZERVASYONLAR
            ==================================================== */}

            {tab === 2 && (
              <Card
                sx={
                  cardStyle
                }
              >
                <CardContent>
                  <SectionTitle
                    title={translate(
                      "reservations"
                    )}

                    action={
                      <Button
                        variant=
                          "contained"

                        onClick={
                          openNewReservation
                        }

                        sx={{
                          borderRadius:
                            99,

                          backgroundColor:
                            COLORS.orange,

                          fontWeight:
                            1000,

                          "&:hover":
                            {
                              backgroundColor:
                                "#EA650D",
                            },
                        }}
                      >
                        ＋{" "}
                        {translate(
                          "newReservation"
                        )}
                      </Button>
                    }
                  />

                  <ResponsiveGrid
                    container
                    spacing={1.5}

                    sx={{
                      marginBottom:
                        2,
                    }}
                  >
                    <ResponsiveGrid
                      xs={12}
                      md={8}
                    >
                      <TextField
                        fullWidth

                        label={translate(
                          "search"
                        )}

                        value={
                          searchText
                        }

                        onChange={(
                          event
                        ) =>
                          setSearchText(
                            event.target.value
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={12}
                      md={4}
                    >
                      <TextField
                        fullWidth
                        select

                        label={translate(
                          "status"
                        )}

                        value={
                          statusFilter
                        }

                        onChange={(
                          event
                        ) =>
                          setStatusFilter(
                            event.target.value
                          )
                        }

                        sx={
                          inputStyle
                        }
                      >
                        <MenuItem
                          value=""
                        >
                          {translate(
                            "all"
                          )}
                        </MenuItem>

                        {RESERVATION_STATUSES.map(
                          (
                            status
                          ) => (
                            <MenuItem
                              key={
                                status
                              }

                              value={
                                status
                              }
                            >
                              {translate(
                                status
                              )}
                            </MenuItem>
                          )
                        )}
                      </TextField>
                    </ResponsiveGrid>
                  </ResponsiveGrid>

                  {filteredReservations.length >
                  0 ? (
                    filteredReservations.map(
                      (
                        reservation
                      ) => (
                        <Paper
                          key={
                            reservation.id
                          }

                          variant=
                            "outlined"

                          sx={{
                            padding:
                              1.7,

                            marginBottom:
                              1.2,

                            borderRadius:
                              3,

                            borderColor:
                              COLORS.border,

                            transition:
                              "transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",

                            "&:hover":
                              {
                                borderColor:
                                  COLORS.orange,

                                transform:
                                  "translateY(-2px)",

                                boxShadow:
                                  "0 10px 28px rgba(15,23,42,0.08)",
                              },
                          }}
                        >
                          <Stack
                            direction={{
                              xs: "column",
                              lg: "row",
                            }}

                            justifyContent=
                              "space-between"

                            spacing={2}
                          >
                            <Box
                              sx={{
                                minWidth:
                                  0,
                              }}
                            >
                              <Stack
                                direction=
                                  "row"

                                spacing={
                                  1
                                }

                                alignItems=
                                  "center"

                                useFlexGap

                                flexWrap=
                                  "wrap"
                              >
                                <Typography
                                  fontWeight={
                                    1000
                                  }
                                >
                                  {reservation.guest_name ||
                                    "-"}
                                </Typography>

                                <StatusChip
                                  value={
                                    reservation.status
                                  }

                                  translate={
                                    translate
                                  }
                                />

                                {reservation.vip_level &&
                                  reservation.vip_level !==
                                    "standard" && (
                                    <Chip
                                      size=
                                        "small"

                                      color=
                                        "warning"

                                      label={String(
                                        reservation.vip_level
                                      ).toUpperCase()}
                                    />
                                  )}
                              </Stack>

                              <Typography
                                sx={{
                                  color:
                                    COLORS.muted,

                                  fontSize:
                                    13,

                                  marginTop:
                                    0.3,
                                }}
                              >
                                {reservation.reservation_no}
                                {" · "}
                                {normalizeDate(
                                  reservation.check_in
                                )}
                                {" → "}
                                {normalizeDate(
                                  reservation.check_out
                                )}
                                {" · "}
                                {reservation.room_no ||
                                  reservationRoomTypeName(
                                    reservation
                                  )}
                              </Typography>

                              <Typography
                                fontWeight={
                                  900
                                }

                                sx={{
                                  marginTop:
                                    0.7,
                                }}
                              >
                                {money(
                                  reservation.grand_total,
                                  reservation.currency,
                                  language
                                )}
                                {" · "}
                                {translate(
                                  "balance"
                                )}
                                {": "}
                                {money(
                                  reservation.balance_total,
                                  reservation.currency,
                                  language
                                )}
                              </Typography>
                            </Box>

                            <Stack
                              direction=
                                "row"

                              spacing={
                                0.7
                              }

                              useFlexGap

                              flexWrap=
                                "wrap"

                              alignItems=
                                "center"
                            >
                              <Button
                                size=
                                  "small"

                                onClick={() =>
                                  openEditReservation(
                                    reservation
                                  )
                                }

                                sx={{
                                  fontWeight:
                                    900,
                                }}
                              >
                                {translate(
                                  "edit"
                                )}
                              </Button>

                              <Button
                                size=
                                  "small"

                                onClick={() =>
                                  openFolio(
                                    reservation
                                  )
                                }

                                sx={{
                                  fontWeight:
                                    900,
                                }}
                              >
                                {translate(
                                  "folio"
                                )}
                              </Button>

                              <Tooltip
                                title={
                                  canCreateGuestInvite(
                                    reservation
                                  )
                                    ? translate(
                                        "guestInviteDescription"
                                      )
                                    : translate(
                                        "guestInviteNotAllowed"
                                      )
                                }
                              >
                                <span>
                                  <Button
                                    size=
                                      "small"

                                    variant=
                                      "outlined"

                                    disabled={
                                      !canCreateGuestInvite(
                                        reservation
                                      )
                                    }

                                    onClick={() =>
                                      openGuestInvite(
                                        reservation
                                      )
                                    }

                                    aria-label={
                                      translate(
                                        "guestInviteButton"
                                      )
                                    }

                                    sx={{
                                      borderColor:
                                        COLORS.orange,

                                      color:
                                        COLORS.orange,

                                      fontWeight:
                                        1000,

                                      whiteSpace:
                                        "nowrap",

                                      "&:hover": {
                                        borderColor:
                                          "#D85F0A",

                                        backgroundColor:
                                          COLORS.orangeLight,
                                      },
                                    }}
                                  >
                                    {"🔐 "}
                                    {translate(
                                      "guestInviteButton"
                                    )}
                                  </Button>
                                </span>
                              </Tooltip>

                              {[
                                "pending",
                                "confirmed",
                              ].includes(
                                reservation.status
                              ) && (
                                <Button
                                  size=
                                    "small"

                                  variant=
                                    "contained"

                                  onClick={() =>
                                    reservationAction(
                                      reservation,
                                      "check-in"
                                    )
                                  }

                                  sx={{
                                    backgroundColor:
                                      COLORS.green,

                                    fontWeight:
                                      900,

                                    "&:hover":
                                      {
                                        backgroundColor:
                                          "#12843D",
                                      },
                                  }}
                                >
                                  {translate(
                                    "checkInAction"
                                  )}
                                </Button>
                              )}

                              {reservation.status ===
                                "checked_in" && (
                                <Button
                                  size=
                                    "small"

                                  variant=
                                    "contained"

                                  onClick={() =>
                                    reservationAction(
                                      reservation,
                                      "check-out"
                                    )
                                  }

                                  sx={{
                                    backgroundColor:
                                      COLORS.blue,

                                    fontWeight:
                                      900,
                                  }}
                                >
                                  {translate(
                                    "checkOutAction"
                                  )}
                                </Button>
                              )}

                              {[
                                "pending",
                                "confirmed",
                                "option",
                                "inquiry",
                              ].includes(
                                reservation.status
                              ) && (
                                <Button
                                  size=
                                    "small"

                                  color=
                                    "error"

                                  onClick={() =>
                                    reservationAction(
                                      reservation,
                                      "cancel"
                                    )
                                  }

                                  sx={{
                                    fontWeight:
                                      900,
                                  }}
                                >
                                  {translate(
                                    "cancelReservation"
                                  )}
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      )
                    )
                  ) : (
                    <EmptyState
                      translate={
                        translate
                      }
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* ====================================================
                ODALAR
            ==================================================== */}

            {tab === 3 && (
              <>
                <SectionTitle
                  title={translate(
                    "rooms"
                  )}

                  action={
                    <Stack
                      direction=
                        "row"

                      spacing={1}

                      useFlexGap

                      flexWrap=
                        "wrap"
                    >
                      <Button
                        onClick={
                          openNewRoomType
                        }

                        sx={{
                          borderRadius:
                            99,

                          fontWeight:
                            1000,
                        }}
                      >
                        {translate(
                          "newRoomType"
                        )}
                      </Button>

                      <Button
                        variant=
                          "contained"

                        onClick={
                          openNewRoom
                        }

                        sx={{
                          borderRadius:
                            99,

                          backgroundColor:
                            COLORS.orange,

                          fontWeight:
                            1000,
                        }}
                      >
                        ＋{" "}
                        {translate(
                          "newRoom"
                        )}
                      </Button>
                    </Stack>
                  }
                />

                <ResponsiveGrid
                  container
                  spacing={2}
                >
                  {rooms.map(
                    (room) => {
                      const roomAccent =
                        room.status ===
                        "vacant_clean"
                          ? COLORS.green
                          : room.status.includes(
                              "occupied"
                            )
                          ? COLORS.orange
                          : room.status ===
                              "vacant_dirty"
                          ? COLORS.amber
                          : COLORS.red;

                      return (
                        <ResponsiveGrid
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}

                          key={
                            room.id
                          }
                        >
                          <Card
                            sx={{
                              ...cardStyle,

                              height:
                                "100%",

                              transition:
                                "transform 0.2s ease, box-shadow 0.2s ease",

                              "&:hover":
                                {
                                  transform:
                                    "translateY(-4px)",

                                  boxShadow:
                                    "0 18px 40px rgba(15,23,42,0.11)",
                                },
                            }}
                          >
                            <Box
                              sx={{
                                height:
                                  7,

                                backgroundColor:
                                  roomAccent,
                              }}
                            />

                            <CardContent>
                              <Stack
                                direction=
                                  "row"

                                justifyContent=
                                  "space-between"

                                spacing={
                                  1
                                }
                              >
                                <Box
                                  sx={{
                                    minWidth:
                                      0,
                                  }}
                                >
                                  <Typography
                                    variant=
                                      "h5"

                                    fontWeight={
                                      1000
                                    }
                                  >
                                    {room.room_no}
                                  </Typography>

                                  <Typography
                                    noWrap

                                    sx={{
                                      color:
                                        COLORS.muted,
                                    }}
                                  >
                                    {room[
                                      `room_type_name_${language}`
                                    ] ||
                                      room.room_type_name_tr ||
                                      "-"}
                                  </Typography>
                                </Box>

                                <Typography
                                  sx={{
                                    fontSize:
                                      30,
                                  }}
                                >
                                  🛏️
                                </Typography>
                              </Stack>

                              <Stack
                                direction=
                                  "row"

                                spacing={
                                  1
                                }

                                useFlexGap

                                flexWrap=
                                  "wrap"

                                sx={{
                                  marginTop:
                                    1,
                                }}
                              >
                                <StatusChip
                                  value={
                                    room.status
                                  }

                                  translate={
                                    translate
                                  }
                                />

                                <Chip
                                  size=
                                    "small"

                                  label={`${translate(
                                    "floor"
                                  )}: ${room.floor_name || "-"}`}
                                />
                              </Stack>

                              <TextField
                                select
                                fullWidth

                                size=
                                  "small"

                                label={translate(
                                  "roomStatus"
                                )}

                                value={
                                  room.status
                                }

                                onChange={(
                                  event
                                ) =>
                                  updateRoomStatus(
                                    room,
                                    event.target.value
                                  )
                                }

                                sx={{
                                  ...inputStyle,

                                  marginTop:
                                    2,
                                }}
                              >
                                {ROOM_STATUSES.map(
                                  (
                                    status
                                  ) => (
                                    <MenuItem
                                      key={
                                        status
                                      }

                                      value={
                                        status
                                      }
                                    >
                                      {translate(
                                        status ===
                                          "maintenance"
                                          ? "maintenanceStatus"
                                          : status
                                      )}
                                    </MenuItem>
                                  )
                                )}
                              </TextField>

                              <Button
                                fullWidth

                                onClick={() =>
                                  openEditRoom(
                                    room
                                  )
                                }

                                sx={{
                                  marginTop:
                                    1,

                                  borderRadius:
                                    99,

                                  fontWeight:
                                    900,
                                }}
                              >
                                {translate(
                                  "edit"
                                )}
                              </Button>
                            </CardContent>
                          </Card>
                        </ResponsiveGrid>
                      );
                    }
                  )}
                </ResponsiveGrid>

                {rooms.length ===
                  0 && (
                  <Card
                    sx={
                      cardStyle
                    }
                  >
                    <EmptyState
                      translate={
                        translate
                      }
                    />
                  </Card>
                )}

                <Card
                  sx={{
                    ...cardStyle,

                    marginTop: 2,
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"

                      fontWeight={1000}

                      sx={{
                        marginBottom:
                          1.5,
                      }}
                    >
                      {translate(
                        "roomTypes"
                      )}
                    </Typography>

                    <ResponsiveGrid
                      container
                      spacing={1.5}
                    >
                      {roomTypes.map(
                        (
                          roomType
                        ) => (
                          <ResponsiveGrid
                            xs={12}
                            sm={6}
                            md={4}

                            key={
                              roomType.id
                            }
                          >
                            <Paper
                              variant=
                                "outlined"

                              sx={{
                                padding:
                                  1.6,

                                borderRadius:
                                  3,

                                height:
                                  "100%",

                                borderColor:
                                  COLORS.border,
                              }}
                            >
                              <Stack
                                direction=
                                  "row"

                                justifyContent=
                                  "space-between"

                                spacing={
                                  1
                                }
                              >
                                <Box
                                  sx={{
                                    minWidth:
                                      0,
                                  }}
                                >
                                  <Typography
                                    fontWeight={
                                      1000
                                    }

                                    noWrap
                                  >
                                    {roomTypeName(
                                      roomType
                                    )}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color:
                                        COLORS.muted,

                                      fontSize:
                                        13,
                                    }}
                                  >
                                    {roomType.room_count ||
                                      0}
                                    {" "}
                                    {translate(
                                      "rooms"
                                    )}
                                    {" · "}
                                    {money(
                                      roomType.base_price,
                                      roomType.currency,
                                      language
                                    )}
                                  </Typography>
                                </Box>

                                <IconButton
                                  onClick={() =>
                                    openEditRoomType(
                                      roomType
                                    )
                                  }
                                >
                                  ✏️
                                </IconButton>
                              </Stack>
                            </Paper>
                          </ResponsiveGrid>
                        )
                      )}
                    </ResponsiveGrid>

                    {roomTypes.length ===
                      0 && (
                      <EmptyState
                        translate={
                          translate
                        }
                      />
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {/* ====================================================
                MİSAFİR CRM
            ==================================================== */}

            {tab === 4 && (
              <Card
                sx={
                  cardStyle
                }
              >
                <CardContent>
                  <SectionTitle
                    title={translate(
                      "guests"
                    )}

                    action={
                      <Button
                        variant=
                          "contained"

                        onClick={
                          openNewGuest
                        }

                        sx={{
                          borderRadius:
                            99,

                          backgroundColor:
                            COLORS.orange,

                          fontWeight:
                            1000,
                        }}
                      >
                        ＋{" "}
                        {translate(
                          "newGuest"
                        )}
                      </Button>
                    }
                  />

                  <TextField
                    fullWidth

                    label={translate(
                      "search"
                    )}

                    value={
                      searchText
                    }

                    onChange={(
                      event
                    ) =>
                      setSearchText(
                        event.target.value
                      )
                    }

                    sx={{
                      ...inputStyle,

                      marginBottom:
                        2,
                    }}
                  />

                  <ResponsiveGrid
                    container
                    spacing={1.5}
                  >
                    {filteredGuests.map(
                      (guest) => (
                        <ResponsiveGrid
                          xs={12}
                          sm={6}
                          md={4}

                          key={
                            guest.id
                          }
                        >
                          <Paper
                            variant=
                              "outlined"

                            sx={{
                              padding:
                                1.7,

                              borderRadius:
                                3,

                              height:
                                "100%",

                              borderColor:
                                COLORS.border,

                              transition:
                                "transform 0.2s ease, border-color 0.2s ease",

                              "&:hover":
                                {
                                  transform:
                                    "translateY(-3px)",

                                  borderColor:
                                    COLORS.orange,
                                },
                            }}
                          >
                            <Stack
                              direction=
                                "row"

                              justifyContent=
                                "space-between"

                              spacing={
                                1
                              }
                            >
                              <Box
                                sx={{
                                  minWidth:
                                    0,
                                }}
                              >
                                <Stack
                                  direction=
                                    "row"

                                  spacing={
                                    0.7
                                  }

                                  alignItems=
                                    "center"

                                  useFlexGap

                                  flexWrap=
                                    "wrap"
                                >
                                  <Typography
                                    fontWeight={
                                      1000
                                    }

                                    noWrap
                                  >
                                    {guest.first_name}
                                    {" "}
                                    {guest.last_name}
                                  </Typography>

                                  {guest.vip_level &&
                                    guest.vip_level !==
                                      "standard" && (
                                      <Chip
                                        size=
                                          "small"

                                        color=
                                          "warning"

                                        label={String(
                                          guest.vip_level
                                        ).toUpperCase()}
                                      />
                                    )}

                                  {guest.blacklisted && (
                                    <Chip
                                      size=
                                        "small"

                                      color=
                                        "error"

                                      label="⚠️"
                                    />
                                  )}
                                </Stack>

                                <Typography
                                  noWrap

                                  sx={{
                                    color:
                                      COLORS.muted,

                                    fontSize:
                                      13,
                                  }}
                                >
                                  {guest.phone ||
                                    "-"}
                                  {" · "}
                                  {guest.email ||
                                    "-"}
                                </Typography>

                                <Typography
                                  sx={{
                                    color:
                                      COLORS.muted,

                                    fontSize:
                                      13,
                                  }}
                                >
                                  {guest.country_code ||
                                    "-"}
                                  {" · "}
                                  {guest.stay_count ||
                                    0}
                                  {" "}
                                  {translate(
                                    "stays"
                                  )}
                                </Typography>

                                {(guest.identity_no_masked ||
                                  guest.passport_no_masked) && (
                                  <Typography
                                    sx={{
                                      color:
                                        COLORS.muted,

                                      fontSize:
                                        12,

                                      marginTop:
                                        0.5,
                                    }}
                                  >
                                    🔐{" "}
                                    {guest.identity_no_masked ||
                                      guest.passport_no_masked}
                                  </Typography>
                                )}
                              </Box>

                              <IconButton
                                onClick={() =>
                                  openEditGuest(
                                    guest
                                  )
                                }
                              >
                                ✏️
                              </IconButton>
                            </Stack>
                          </Paper>
                        </ResponsiveGrid>
                      )
                    )}
                  </ResponsiveGrid>

                  {filteredGuests.length ===
                    0 && (
                    <EmptyState
                      translate={
                        translate
                      }
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* ====================================================
                HOUSEKEEPING
            ==================================================== */}

            {tab === 5 && (
              <Card
                sx={
                  cardStyle
                }
              >
                <CardContent>
                  <SectionTitle
                    title={translate(
                      "housekeeping"
                    )}

                    action={
                      <Button
                        variant=
                          "contained"

                        onClick={
                          openNewHousekeepingTask
                        }

                        sx={{
                          borderRadius:
                            99,

                          backgroundColor:
                            COLORS.orange,

                          fontWeight:
                            1000,
                        }}
                      >
                        ＋{" "}
                        {translate(
                          "newTask"
                        )}
                      </Button>
                    }
                  />

                  {housekeeping.length >
                  0 ? (
                    housekeeping.map(
                      (task) => (
                        <Paper
                          key={
                            task.id
                          }

                          variant=
                            "outlined"

                          sx={{
                            padding:
                              1.6,

                            borderRadius:
                              3,

                            marginBottom:
                              1,

                            borderColor:
                              COLORS.border,
                          }}
                        >
                          <Stack
                            direction={{
                              xs: "column",
                              sm: "row",
                            }}

                            justifyContent=
                              "space-between"

                            spacing={1}
                          >
                            <Box
                              sx={{
                                minWidth:
                                  0,
                              }}
                            >
                              <Typography
                                fontWeight={
                                  1000
                                }
                              >
                                {translate(
                                  "roomNo"
                                )}
                                {" "}
                                {task.room_no}
                                {" · "}
                                {translate(
                                  task.task_type
                                )}
                              </Typography>

                              <Typography
                                sx={{
                                  color:
                                    COLORS.muted,

                                  fontSize:
                                    13,
                                }}
                              >
                                {normalizeDate(
                                  task.task_date
                                )}
                                {" · "}
                                {task.assigned_name ||
                                  "-"}
                                {task.notes
                                  ? ` · ${task.notes}`
                                  : ""}
                              </Typography>
                            </Box>

                            <Stack
                              direction=
                                "row"

                              spacing={
                                1
                              }

                              useFlexGap

                              flexWrap=
                                "wrap"

                              alignItems=
                                "center"
                            >
                              <StatusChip
                                value={
                                  task.priority
                                }

                                translate={
                                  translate
                                }
                              />

                              <StatusChip
                                value={
                                  task.status
                                }

                                translate={
                                  translate
                                }
                              />

                              {task.status ===
                                "pending" && (
                                <Button
                                  size=
                                    "small"

                                  onClick={() =>
                                    updateHousekeepingTask(
                                      task,
                                      "in_progress"
                                    )
                                  }
                                >
                                  {translate(
                                    "in_progress"
                                  )}
                                </Button>
                              )}

                              {![
                                "completed",
                                "inspected",
                                "cancelled",
                              ].includes(
                                task.status
                              ) && (
                                <Button
                                  size=
                                    "small"

                                  variant=
                                    "contained"

                                  onClick={() =>
                                    updateHousekeepingTask(
                                      task,
                                      "completed"
                                    )
                                  }

                                  sx={{
                                    backgroundColor:
                                      COLORS.green,

                                    fontWeight:
                                      900,
                                  }}
                                >
                                  {translate(
                                    "completed"
                                  )}
                                </Button>
                              )}

                              {task.status ===
                                "completed" && (
                                <Button
                                  size=
                                    "small"

                                  variant=
                                    "contained"

                                  onClick={() =>
                                    updateHousekeepingTask(
                                      task,
                                      "inspected"
                                    )
                                  }

                                  sx={{
                                    backgroundColor:
                                      COLORS.blue,

                                    fontWeight:
                                      900,
                                  }}
                                >
                                  {translate(
                                    "inspected"
                                  )}
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      )
                    )
                  ) : (
                    <EmptyState
                      translate={
                        translate
                      }
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* ====================================================
                TEKNİK SERVİS
            ==================================================== */}

            {tab === 6 && (
              <Card
                sx={
                  cardStyle
                }
              >
                <CardContent>
                  <SectionTitle
                    title={translate(
                      "maintenance"
                    )}

                    action={
                      <Button
                        variant=
                          "contained"

                        onClick={
                          openNewMaintenanceIssue
                        }

                        sx={{
                          borderRadius:
                            99,

                          backgroundColor:
                            COLORS.orange,

                          fontWeight:
                            1000,
                        }}
                      >
                        ＋{" "}
                        {translate(
                          "newMaintenance"
                        )}
                      </Button>
                    }
                  />

                  {maintenance.length >
                  0 ? (
                    maintenance.map(
                      (issue) => (
                        <Paper
                          key={
                            issue.id
                          }

                          variant=
                            "outlined"

                          sx={{
                            padding:
                              1.6,

                            borderRadius:
                              3,

                            marginBottom:
                              1,

                            borderColor:
                              COLORS.border,
                          }}
                        >
                          <Stack
                            direction={{
                              xs: "column",
                              sm: "row",
                            }}

                            justifyContent=
                              "space-between"

                            spacing={1}
                          >
                            <Box
                              sx={{
                                minWidth:
                                  0,
                              }}
                            >
                              <Typography
                                fontWeight={
                                  1000
                                }
                              >
                                {issue.title}

                                {issue.room_no
                                  ? ` · ${translate(
                                      "roomNo"
                                    )} ${issue.room_no}`
                                  : ""}
                              </Typography>

                              <Typography
                                sx={{
                                  color:
                                    COLORS.muted,

                                  fontSize:
                                    13,
                                }}
                              >
                                {issue.category}
                                {" · "}
                                {issue.assigned_name ||
                                  "-"}
                                {" · "}
                                {money(
                                  issue.actual_cost ||
                                    issue.estimated_cost ||
                                    0,

                                  property.default_currency,

                                  language
                                )}
                              </Typography>

                              {issue.description && (
                                <Typography
                                  sx={{
                                    fontSize:
                                      13,

                                    marginTop:
                                      0.5,
                                  }}
                                >
                                  {issue.description}
                                </Typography>
                              )}

                              {issue.room_blocked && (
                                <Chip
                                  size=
                                    "small"

                                  color=
                                    "error"

                                  label={translate(
                                    "roomBlocked"
                                  )}

                                  sx={{
                                    marginTop:
                                      1,
                                  }}
                                />
                              )}
                            </Box>

                            <Stack
                              direction=
                                "row"

                              spacing={
                                1
                              }

                              useFlexGap

                              flexWrap=
                                "wrap"

                              alignItems=
                                "center"
                            >
                              <StatusChip
                                value={
                                  issue.priority
                                }

                                translate={
                                  translate
                                }
                              />

                              <StatusChip
                                value={
                                  issue.status
                                }

                                translate={
                                  translate
                                }
                              />

                              {issue.status !==
                                "completed" && (
                                <Button
                                  size=
                                    "small"

                                  variant=
                                    "contained"

                                  onClick={() =>
                                    updateMaintenanceIssue(
                                      issue,
                                      "completed"
                                    )
                                  }

                                  sx={{
                                    backgroundColor:
                                      COLORS.green,

                                    fontWeight:
                                      900,
                                  }}
                                >
                                  {translate(
                                    "completed"
                                  )}
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      )
                    )
                  ) : (
                    <EmptyState
                      translate={
                        translate
                      }
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* ====================================================
                FOLYO VE ÖDEMELER
            ==================================================== */}

            {tab === 7 && (
              <Card
                sx={
                  cardStyle
                }
              >
                <CardContent>
                  <SectionTitle
                    title={translate(
                      "finance"
                    )}
                  />

                  {reservations.filter(
                    (
                      reservation
                    ) =>
                      Number(
                        reservation.grand_total ||
                        0
                      ) > 0
                  ).length >
                  0 ? (
                    reservations
                      .filter(
                        (
                          reservation
                        ) =>
                          Number(
                            reservation.grand_total ||
                            0
                          ) > 0
                      )
                      .map(
                        (
                          reservation
                        ) => (
                          <Paper
                            key={
                              reservation.id
                            }

                            variant=
                              "outlined"

                            sx={{
                              padding:
                                1.6,

                              borderRadius:
                                3,

                              marginBottom:
                                1,

                              borderColor:
                                COLORS.border,
                            }}
                          >
                            <Stack
                              direction={{
                                xs: "column",
                                sm: "row",
                              }}

                              justifyContent=
                                "space-between"

                              alignItems={{
                                xs: "stretch",
                                sm: "center",
                              }}

                              spacing={1}
                            >
                              <Box
                                sx={{
                                  minWidth:
                                    0,
                                }}
                              >
                                <Typography
                                  fontWeight={
                                    1000
                                  }
                                >
                                  {reservation.guest_name}
                                  {" · "}
                                  {reservation.reservation_no}
                                </Typography>

                                <Typography
                                  sx={{
                                    color:
                                      COLORS.muted,
                                  }}
                                >
                                  {translate(
                                    "total"
                                  )}
                                  {": "}
                                  {money(
                                    reservation.grand_total,
                                    reservation.currency,
                                    language
                                  )}
                                  {" · "}
                                  {translate(
                                    "paid"
                                  )}
                                  {": "}
                                  {money(
                                    reservation.paid_total,
                                    reservation.currency,
                                    language
                                  )}
                                  {" · "}
                                  {translate(
                                    "balance"
                                  )}
                                  {": "}
                                  {money(
                                    reservation.balance_total,
                                    reservation.currency,
                                    language
                                  )}
                                </Typography>
                              </Box>

                              <Button
                                variant=
                                  "contained"

                                onClick={() =>
                                  openFolio(
                                    reservation
                                  )
                                }

                                sx={{
                                  backgroundColor:
                                    COLORS.blue,

                                  borderRadius:
                                    99,

                                  fontWeight:
                                    900,
                                }}
                              >
                                {translate(
                                  "folio"
                                )}
                              </Button>
                            </Stack>
                          </Paper>
                        )
                      )
                  ) : (
                    <EmptyState
                      translate={
                        translate
                      }
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* ====================================================
                RAPORLAR
            ==================================================== */}

            {tab === 8 && (
              <>
                <Card
                  sx={{
                    ...cardStyle,

                    marginBottom: 2,
                  }}
                >
                  <CardContent>
                    <SectionTitle
                      title={translate(
                        "reportPeriod"
                      )}

                      action={
                        <Button
                          variant=
                            "contained"

                          onClick={() =>
                            loadReport()
                          }

                          sx={{
                            borderRadius:
                              99,

                            backgroundColor:
                              COLORS.orange,

                            fontWeight:
                              1000,
                          }}
                        >
                          {translate(
                            "refresh"
                          )}
                        </Button>
                      }
                    />

                    <ResponsiveGrid
                      container
                      spacing={2}
                    >
                      <ResponsiveGrid
                        xs={12}
                        sm={6}
                      >
                        <TextField
                          fullWidth

                          type="date"

                          label={translate(
                            "checkIn"
                          )}

                          value={
                            reportFrom
                          }

                          onChange={(
                            event
                          ) =>
                            setReportFrom(
                              event.target.value
                            )
                          }

                          InputLabelProps={{
                            shrink: true,
                          }}

                          sx={
                            inputStyle
                          }
                        />
                      </ResponsiveGrid>

                      <ResponsiveGrid
                        xs={12}
                        sm={6}
                      >
                        <TextField
                          fullWidth

                          type="date"

                          label={translate(
                            "checkOut"
                          )}

                          value={
                            reportTo
                          }

                          onChange={(
                            event
                          ) =>
                            setReportTo(
                              event.target.value
                            )
                          }

                          InputLabelProps={{
                            shrink: true,
                          }}

                          sx={
                            inputStyle
                          }
                        />
                      </ResponsiveGrid>
                    </ResponsiveGrid>
                  </CardContent>
                </Card>

                <ResponsiveGrid
                  container
                  spacing={2}
                >
                  <ResponsiveGrid
                    xs={6}
                    md={3}
                  >
                    <KpiCard
                      icon="📊"

                      label={translate(
                        "occupancy"
                      )}

                      value={`${Number(
                        reportSummary.occupancy_rate ||
                        0
                      ).toFixed(1)}%`}
                    />
                  </ResponsiveGrid>

                  <ResponsiveGrid
                    xs={6}
                    md={3}
                  >
                    <KpiCard
                      icon="💶"

                      label={translate(
                        "adr"
                      )}

                      value={money(
                        reportSummary.adr ||
                        0,

                        property.default_currency,

                        language
                      )}

                      accent={
                        COLORS.orange
                      }
                    />
                  </ResponsiveGrid>

                  <ResponsiveGrid
                    xs={6}
                    md={3}
                  >
                    <KpiCard
                      icon="🏨"

                      label={translate(
                        "revpar"
                      )}

                      value={money(
                        reportSummary.revpar ||
                        0,

                        property.default_currency,

                        language
                      )}

                      accent={
                        COLORS.green
                      }
                    />
                  </ResponsiveGrid>

                  <ResponsiveGrid
                    xs={6}
                    md={3}
                  >
                    <KpiCard
                      icon="❌"

                      label={translate(
                        "cancellationRate"
                      )}

                      value={`${Number(
                        reportSummary.cancellation_rate ||
                        0
                      ).toFixed(1)}%`}

                      accent={
                        COLORS.red
                      }
                    />
                  </ResponsiveGrid>

                  <ResponsiveGrid
                    xs={6}
                    md={3}
                  >
                    <KpiCard
                      icon="🛎️"

                      label={translate(
                        "totalReservations"
                      )}

                      value={
                        reportSummary.reservation_count ||
                        0
                      }

                      accent={
                        COLORS.purple
                      }
                    />
                  </ResponsiveGrid>

                  <ResponsiveGrid
                    xs={6}
                    md={3}
                  >
                    <KpiCard
                      icon="🌙"

                      label={translate(
                        "roomNights"
                      )}

                      value={
                        reportSummary.room_nights ||
                        0
                      }

                      accent={
                        COLORS.blue
                      }
                    />
                  </ResponsiveGrid>

                  <ResponsiveGrid
                    xs={6}
                    md={3}
                  >
                    <KpiCard
                      icon="💰"

                      label={translate(
                        "collectedRevenue"
                      )}

                      value={money(
                        reportSummary.collected_revenue ||
                        0,

                        property.default_currency,

                        language
                      )}

                      accent={
                        COLORS.green
                      }
                    />
                  </ResponsiveGrid>

                  <ResponsiveGrid
                    xs={6}
                    md={3}
                  >
                    <KpiCard
                      icon="🗓️"

                      label={translate(
                        "averageStay"
                      )}

                      value={Number(
                        reportSummary.average_stay ||
                        0
                      ).toFixed(1)}

                      accent={
                        COLORS.amber
                      }
                    />
                  </ResponsiveGrid>
                </ResponsiveGrid>

                <ResponsiveGrid
                  container
                  spacing={2}

                  sx={{
                    marginTop: 0.2,
                  }}
                >
                  <ResponsiveGrid
                    xs={12}
                    md={4}
                  >
                    <Card
                      sx={{
                        ...cardStyle,

                        height: "100%",
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant=
                            "h6"

                          fontWeight={
                            1000
                          }

                          sx={{
                            marginBottom:
                              1.5,
                          }}
                        >
                          {translate(
                            "source"
                          )}
                        </Typography>

                        {(reportData.sources ||
                          []).map(
                          (item) => (
                            <Stack
                              key={
                                item.label
                              }

                              direction=
                                "row"

                              justifyContent=
                                "space-between"

                              sx={{
                                paddingY:
                                  1,

                                borderBottom:
                                  `1px solid ${COLORS.border}`,
                              }}
                            >
                              <Typography>
                                {translate(
                                  item.label ===
                                    "website"
                                    ? "websiteSource"
                                    : item.label ===
                                      "phone"
                                    ? "phoneSource"
                                    : item.label
                                )}
                              </Typography>

                              <Typography
                                fontWeight={
                                  1000
                                }
                              >
                                {item.value}
                              </Typography>
                            </Stack>
                          )
                        )}

                        {(reportData.sources ||
                          []).length ===
                          0 && (
                          <EmptyState
                            translate={
                              translate
                            }
                          />
                        )}
                      </CardContent>
                    </Card>
                  </ResponsiveGrid>

                  <ResponsiveGrid
                    xs={12}
                    md={4}
                  >
                    <Card
                      sx={{
                        ...cardStyle,

                        height: "100%",
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant=
                            "h6"

                          fontWeight={
                            1000
                          }

                          sx={{
                            marginBottom:
                              1.5,
                          }}
                        >
                          {translate(
                            "roomTypes"
                          )}
                        </Typography>

                        {(reportData.room_types ||
                          []).map(
                          (item) => (
                            <Stack
                              key={
                                item.label
                              }

                              direction=
                                "row"

                              justifyContent=
                                "space-between"

                              sx={{
                                paddingY:
                                  1,

                                borderBottom:
                                  `1px solid ${COLORS.border}`,
                              }}
                            >
                              <Typography>
                                {item.label}
                              </Typography>

                              <Box
                                sx={{
                                  textAlign:
                                    "right",
                                }}
                              >
                                <Typography
                                  fontWeight={
                                    1000
                                  }
                                >
                                  {item.value}
                                </Typography>

                                <Typography
                                  sx={{
                                    color:
                                      COLORS.muted,

                                    fontSize:
                                      12,
                                  }}
                                >
                                  {money(
                                    item.revenue,
                                    property.default_currency,
                                    language
                                  )}
                                </Typography>
                              </Box>
                            </Stack>
                          )
                        )}

                        {(reportData.room_types ||
                          []).length ===
                          0 && (
                          <EmptyState
                            translate={
                              translate
                            }
                          />
                        )}
                      </CardContent>
                    </Card>
                  </ResponsiveGrid>

                  <ResponsiveGrid
                    xs={12}
                    md={4}
                  >
                    <Card
                      sx={{
                        ...cardStyle,

                        height: "100%",
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant=
                            "h6"

                          fontWeight={
                            1000
                          }

                          sx={{
                            marginBottom:
                              1.5,
                          }}
                        >
                          {translate(
                            "country"
                          )}
                        </Typography>

                        {(reportData.countries ||
                          []).map(
                          (item) => (
                            <Stack
                              key={
                                item.label
                              }

                              direction=
                                "row"

                              justifyContent=
                                "space-between"

                              sx={{
                                paddingY:
                                  1,

                                borderBottom:
                                  `1px solid ${COLORS.border}`,
                              }}
                            >
                              <Typography>
                                {item.label}
                              </Typography>

                              <Typography
                                fontWeight={
                                  1000
                                }
                              >
                                {item.value}
                              </Typography>
                            </Stack>
                          )
                        )}

                        {(reportData.countries ||
                          []).length ===
                          0 && (
                          <EmptyState
                            translate={
                              translate
                            }
                          />
                        )}
                      </CardContent>
                    </Card>
                  </ResponsiveGrid>
                </ResponsiveGrid>
              </>
            )}

            {/* ====================================================
                OTEL AYARLARI
            ==================================================== */}

            {tab === 9 && (
              <Card
                sx={
                  cardStyle
                }
              >
                <CardContent>
                  <SectionTitle
                    title={translate(
                      "settings"
                    )}

                    action={
                      <Button
                        variant=
                          "contained"

                        onClick={
                          saveProperty
                        }

                        sx={{
                          borderRadius:
                            99,

                          backgroundColor:
                            COLORS.orange,

                          fontWeight:
                            1000,
                        }}
                      >
                        {translate(
                          "save"
                        )}
                      </Button>
                    }
                  />

                  <Typography
                    variant="h6"

                    fontWeight={1000}

                    sx={{
                      marginBottom:
                        1.5,
                    }}
                  >
                    {translate(
                      "hotelInformation"
                    )}
                  </Typography>

                  <ResponsiveGrid
                    container
                    spacing={2}
                  >
                    <ResponsiveGrid
                      xs={12}
                      md={6}
                    >
                      <TextField
                        fullWidth

                        label={translate(
                          "propertyName"
                        )}

                        value={
                          property.name ||
                          ""
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              name:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={12}
                      md={6}
                    >
                      <TextField
                        fullWidth

                        label={translate(
                          "legalName"
                        )}

                        value={
                          property.legal_name ||
                          ""
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              legal_name:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={12}
                      md={4}
                    >
                      <TextField
                        fullWidth

                        label={translate(
                          "propertyCode"
                        )}

                        value={
                          property.property_code ||
                          ""
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              property_code:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={12}
                      md={4}
                    >
                      <TextField
                        fullWidth

                        label={translate(
                          "city"
                        )}

                        value={
                          property.city ||
                          ""
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              city:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={12}
                      md={4}
                    >
                      <TextField
                        fullWidth

                        label={translate(
                          "district"
                        )}

                        value={
                          property.district ||
                          ""
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              district:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={12}
                    >
                      <TextField
                        fullWidth

                        multiline

                        minRows={2}

                        label={translate(
                          "address"
                        )}

                        value={
                          property.address ||
                          ""
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              address:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={12}
                      md={4}
                    >
                      <TextField
                        fullWidth

                        label={translate(
                          "phone"
                        )}

                        value={
                          property.phone ||
                          ""
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              phone:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={12}
                      md={4}
                    >
                      <TextField
                        fullWidth

                        label={translate(
                          "email"
                        )}

                        value={
                          property.email ||
                          ""
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              email:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={12}
                      md={4}
                    >
                      <TextField
                        fullWidth

                        label={translate(
                          "website"
                        )}

                        value={
                          property.website ||
                          ""
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              website:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={6}
                      md={3}
                    >
                      <TextField
                        fullWidth

                        type="time"

                        label={translate(
                          "checkInTime"
                        )}

                        value={String(
                          property.check_in_time ||
                          "14:00"
                        ).slice(
                          0,
                          5
                        )}

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              check_in_time:
                                event.target.value,
                            })
                          )
                        }

                        InputLabelProps={{
                          shrink: true,
                        }}

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={6}
                      md={3}
                    >
                      <TextField
                        fullWidth

                        type="time"

                        label={translate(
                          "checkOutTime"
                        )}

                        value={String(
                          property.check_out_time ||
                          "12:00"
                        ).slice(
                          0,
                          5
                        )}

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              check_out_time:
                                event.target.value,
                            })
                          )
                        }

                        InputLabelProps={{
                          shrink: true,
                        }}

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={6}
                      md={3}
                    >
                      <TextField
                        fullWidth

                        type="number"

                        label={translate(
                          "tax"
                        )}

                        value={
                          property.tax_percent ||
                          0
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              tax_percent:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>

                    <ResponsiveGrid
                      xs={6}
                      md={3}
                    >
                      <TextField
                        fullWidth

                        type="number"

                        label={translate(
                          "accommodationTax"
                        )}

                        value={
                          property.accommodation_tax_percent ||
                          0
                        }

                        onChange={(
                          event
                        ) =>
                          setProperty(
                            (
                              previous
                            ) => ({
                              ...previous,

                              accommodation_tax_percent:
                                event.target.value,
                            })
                          )
                        }

                        sx={
                          inputStyle
                        }
                      />
                    </ResponsiveGrid>
                  </ResponsiveGrid>

                  <Divider
                    sx={{
                      marginY: 3,
                    }}
                  />

                  <Typography
                    variant="h6"

                    fontWeight={1000}

                    sx={{
                      marginBottom:
                        1.5,
                    }}
                  >
                    {translate(
                      "moduleSettings"
                    )}
                  </Typography>

                  <ResponsiveGrid
                    container
                    spacing={1.5}
                  >
                    {[
                      [
                        "booking_engine_enabled",
                        "bookingEngine",
                      ],

                      [
                        "channel_manager_enabled",
                        "channelManager",
                      ],

                      [
                        "qr_room_guide_enabled",
                        "qrGuide",
                      ],

                      [
                        "whatsapp_enabled",
                        "whatsapp",
                      ],

                      [
                        "housekeeping_enabled",
                        "housekeeping",
                      ],

                      [
                        "maintenance_enabled",
                        "maintenance",
                      ],
                    ].map(
                      ([
                        key,
                        label,
                      ]) => (
                        <ResponsiveGrid
                          xs={12}
                          sm={6}

                          key={
                            key
                          }
                        >
                          <Paper
                            variant=
                              "outlined"

                            sx={{
                              padding:
                                1.5,

                              borderRadius:
                                3,

                              borderColor:
                                COLORS.border,
                            }}
                          >
                            <Stack
                              direction=
                                "row"

                              justifyContent=
                                "space-between"

                              alignItems=
                                "center"

                              spacing={
                                1
                              }
                            >
                              <Typography
                                fontWeight={
                                  900
                                }
                              >
                                {translate(
                                  label
                                )}
                              </Typography>

                              <Switch
                                checked={
                                  Boolean(
                                    property[
                                      key
                                    ]
                                  )
                                }

                                onChange={(
                                  event
                                ) =>
                                  setProperty(
                                    (
                                      previous
                                    ) => ({
                                      ...previous,

                                      [key]:
                                        event.target.checked,
                                    })
                                  )
                                }
                              />
                            </Stack>
                          </Paper>
                        </ResponsiveGrid>
                      )
                    )}
                  </ResponsiveGrid>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Box>

      {/* ========================================================
          MİSAFİR UYGULAMA DAVETİ
      ======================================================== */}

      <Dialog
        open={guestInviteDialog}

        onClose={closeGuestInvite}

        fullWidth

        maxWidth="sm"

        fullScreen={mobile}

        PaperProps={{
          sx: {
            borderRadius: mobile
              ? 0
              : 4,

            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            padding: {
              xs: 2,
              sm: 2.5,
            },

            color: "white",

            background:
              `linear-gradient(135deg, ${COLORS.blueDark}, ${COLORS.blue} 62%, ${COLORS.orange})`,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                flexShrink: 0,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                backgroundColor:
                  "rgba(255,255,255,0.16)",
                border:
                  "1px solid rgba(255,255,255,0.24)",
                fontSize: 23,
              }}
            >
              🔐
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="div"
                sx={{
                  fontSize: {
                    xs: 18,
                    sm: 21,
                  },
                  fontWeight: 1000,
                  lineHeight: 1.2,
                }}
              >
                {translate(
                  "guestInviteTitle"
                )}
              </Typography>

              <Typography
                component="div"
                sx={{
                  marginTop: 0.35,
                  color:
                    "rgba(255,255,255,0.82)",
                  fontSize: 12.5,
                  fontWeight: 700,
                  lineHeight: 1.35,
                }}
              >
                {translate(
                  "guestInviteDescription"
                )}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers

          sx={{
            padding: {
              xs: 1.5,
              sm: 2.5,
            },

            backgroundColor:
              COLORS.background,
          }}
        >
          <Paper
            variant="outlined"

            sx={{
              padding: 1.7,
              borderRadius: 3,
              borderColor:
                COLORS.border,
              backgroundColor:
                COLORS.surface,
            }}
          >
            <ResponsiveGrid
              container
              spacing={1.2}
            >
              {[
                [
                  translate(
                    "guestInviteGuest"
                  ),
                  guestInviteReservation
                    ?.guest_name ||
                    "-",
                ],
                [
                  translate(
                    "guestInviteReservation"
                  ),
                  guestInviteReservation
                    ?.reservation_no ||
                    "-",
                ],
                [
                  translate(
                    "guestInviteRoom"
                  ),
                  guestInviteReservation
                    ?.room_no ||
                    (guestInviteReservation
                      ? reservationRoomTypeName(
                          guestInviteReservation
                        )
                      : "-"),
                ],
              ].map(
                ([label, value]) => (
                  <ResponsiveGrid
                    key={label}
                    xs={12}
                    sm={4}
                  >
                    <Typography
                      sx={{
                        color:
                          COLORS.muted,
                        fontSize: 11,
                        fontWeight: 900,
                        textTransform:
                          "uppercase",
                        letterSpacing:
                          0.45,
                      }}
                    >
                      {label}
                    </Typography>

                    <Typography
                      title={String(
                        value
                      )}
                      sx={{
                        marginTop: 0.35,
                        color:
                          COLORS.text,
                        fontSize: 14,
                        fontWeight: 1000,
                        overflow: "hidden",
                        textOverflow:
                          "ellipsis",
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {value}
                    </Typography>
                  </ResponsiveGrid>
                )
              )}
            </ResponsiveGrid>
          </Paper>

          {!guestInvite ? (
            <Alert
              severity="info"

              sx={{
                marginTop: 2,
                borderRadius: 3,
                alignItems: "center",
                fontWeight: 750,
                lineHeight: 1.55,
              }}
            >
              {translate(
                "guestInviteSecurityNote"
              )}
            </Alert>
          ) : (
            <Stack
              spacing={1.5}
              sx={{ marginTop: 2 }}
            >
              <Alert
                severity="success"

                sx={{
                  borderRadius: 3,
                  fontWeight: 900,
                }}
              >
                {translate(
                  "guestInviteCreated"
                )}
              </Alert>

              <Paper
                variant="outlined"

                sx={{
                  padding: {
                    xs: 1.5,
                    sm: 2,
                  },
                  borderRadius: 3,
                  borderColor:
                    `${COLORS.orange}55`,
                  background:
                    `linear-gradient(145deg, ${COLORS.orangeLight}, #FFFFFF 68%)`,
                }}
              >
                <Typography
                  sx={{
                    color:
                      COLORS.amber,
                    fontSize: 11,
                    fontWeight: 1000,
                    textTransform:
                      "uppercase",
                    letterSpacing: 0.55,
                  }}
                >
                  {translate(
                    "guestInviteCode"
                  )}
                </Typography>

                <Typography
                  component="code"
                  sx={{
                    display: "block",
                    marginTop: 1,
                    padding: 1.4,
                    borderRadius: 2.5,
                    color:
                      COLORS.blueDark,
                    backgroundColor:
                      "rgba(255,255,255,0.88)",
                    border:
                      `1px dashed ${COLORS.orange}`,
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    fontSize: {
                      xs: 34,
                      sm: 42,
                    },
                    fontWeight: 900,
                    lineHeight: 1.25,
                    letterSpacing: {
                      xs: 7,
                      sm: 10,
                    },
                    overflowWrap:
                      "anywhere",
                    userSelect: "all",
                    textAlign: "center",
                  }}
                >
                  {guestInvite.invite_token}
                </Typography>
              </Paper>

              {guestInvite.qr_data_url && (
                <Paper
                  variant="outlined"
                  sx={{
                    padding: 2,
                    borderRadius: 3,
                    borderColor: COLORS.border,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.2,
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Box
                    component="img"
                    src={guestInvite.qr_data_url}
                    alt={translate("guestInviteQr")}
                    sx={{
                      display: "block",
                      width: {
                        xs: 210,
                        sm: 250,
                      },
                      maxWidth: "100%",
                      aspectRatio: "1 / 1",
                    }}
                  />

                  <Typography
                    sx={{
                      maxWidth: 440,
                      color: COLORS.muted,
                      fontSize: 12.5,
                      fontWeight: 800,
                      lineHeight: 1.5,
                      textAlign: "center",
                    }}
                  >
                    {translate("guestInviteQr")}
                  </Typography>
                </Paper>
              )}

              {guestInvite.deep_link && (
                <Paper
                  variant="outlined"

                  sx={{
                    padding: 1.5,
                    borderRadius: 3,
                    borderColor:
                      COLORS.border,
                  }}
                >
                  <Typography
                    sx={{
                      color:
                        COLORS.muted,
                      fontSize: 11,
                      fontWeight: 900,
                      textTransform:
                        "uppercase",
                    }}
                  >
                    {translate(
                      "guestInviteLink"
                    )}
                  </Typography>

                  <Typography
                    sx={{
                      marginTop: 0.6,
                      color:
                        COLORS.blue,
                      fontSize: 12.5,
                      fontWeight: 800,
                      overflowWrap:
                        "anywhere",
                      userSelect: "all",
                    }}
                  >
                    {guestInvite.deep_link}
                  </Typography>
                </Paper>
              )}

              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={1}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    padding: 1.4,
                    borderRadius: 3,
                    borderColor:
                      COLORS.border,
                  }}
                >
                  <Typography
                    sx={{
                      color:
                        COLORS.muted,
                      fontSize: 11,
                      fontWeight: 900,
                      textTransform:
                        "uppercase",
                    }}
                  >
                    {translate(
                      "guestInviteExpires"
                    )}
                  </Typography>

                  <Typography
                    sx={{
                      marginTop: 0.4,
                      color:
                        COLORS.text,
                      fontSize: 13.5,
                      fontWeight: 1000,
                    }}
                  >
                    {formatDateTime(
                      guestInvite.expires_at,
                      language
                    )}
                  </Typography>
                </Paper>

                <Alert
                  severity="warning"
                  sx={{
                    flex: 2,
                    borderRadius: 3,
                    alignItems:
                      "center",
                    fontSize: 12.5,
                    fontWeight: 750,
                    lineHeight: 1.45,
                  }}
                >
                  {translate(
                    "guestInviteSecurityNote"
                  )}
                </Alert>
              </Stack>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: {
              xs: 1.5,
              sm: 2,
            },
            backgroundColor:
              COLORS.surface,
          }}
        >
          <Stack
            direction={{
              xs: "column-reverse",
              sm: "row",
            }}
            spacing={1}
            useFlexGap
            sx={{ width: "100%" }}
          >
            <Button
              onClick={closeGuestInvite}

              disabled={
                guestInviteLoading
              }

              sx={{
                fontWeight: 900,
              }}
            >
              {translate("close")}
            </Button>

            <Box
              sx={{
                display: {
                  xs: "none",
                  sm: "block",
                },
                flex: 1,
              }}
            />

            {!guestInvite ? (
              <Button
                variant="contained"

                onClick={
                  createGuestInvite
                }

                disabled={
                  guestInviteLoading
                }

                startIcon={
                  guestInviteLoading ? (
                    <CircularProgress
                      size={17}
                      color="inherit"
                    />
                  ) : (
                    <span>🔐</span>
                  )
                }

                sx={{
                  backgroundColor:
                    COLORS.blueDark,
                  fontWeight: 1000,
                  "&:hover": {
                    backgroundColor:
                      COLORS.blue,
                  },
                }}
              >
                {guestInviteLoading
                  ? translate(
                      "guestInviteGenerating"
                    )
                  : translate(
                      "guestInviteGenerate"
                    )}
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={() =>
                    copyGuestInviteValue(
                      guestInvite.invite_token
                    )
                  }
                  sx={{
                    borderColor:
                      COLORS.blue,
                    color: COLORS.blue,
                    fontWeight: 1000,
                  }}
                >
                  {"📋 "}
                  {translate(
                    "guestInviteCopyCode"
                  )}
                </Button>

                {guestInvite.deep_link && (
                  <Button
                    variant="outlined"
                    onClick={() =>
                      copyGuestInviteValue(
                        guestInvite.deep_link
                      )
                    }
                    sx={{
                      borderColor:
                        COLORS.orange,
                      color:
                        COLORS.orange,
                      fontWeight: 1000,
                    }}
                  >
                    {"🔗 "}
                    {translate(
                      "guestInviteCopyLink"
                    )}
                  </Button>
                )}

                <Button
                  variant="contained"
                  onClick={
                    sendGuestInviteWhatsApp
                  }
                  sx={{
                    backgroundColor:
                      "#128C4A",
                    fontWeight: 1000,
                    "&:hover": {
                      backgroundColor:
                        "#0E713B",
                    },
                  }}
                >
                  {"💬 "}
                  {translate(
                    "guestInviteWhatsApp"
                  )}
                </Button>
              </>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      {

/* ========================================================
          REZERVASYON DİYALOĞU
      ======================================================== */}

      <Dialog
        open={
          reservationDialog
        }

        onClose={() =>
          setReservationDialog(
            false
          )
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          mobile
        }
      >
        <DialogTitle
          fontWeight={1000}
        >
          {reservationForm.id
            ? translate(
                "editReservation"
              )
            : translate(
                "newReservation"
              )}
        </DialogTitle>

        <DialogContent
          dividers
        >
          <ResponsiveGrid
            container
            spacing={2}
          >
            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "guest"
                )}

                value={
                  reservationForm.guest_id
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      guest_id:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                <MenuItem
                  value=""
                >
                  {translate(
                    "selectGuest"
                  )}
                </MenuItem>

                {guests.map(
                  (guest) => (
                    <MenuItem
                      key={
                        guest.id
                      }

                      value={
                        guest.id
                      }
                    >
                      {guest.first_name}
                      {" "}
                      {guest.last_name}
                      {" · "}
                      {guest.phone ||
                        "-"}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "roomType"
                )}

                value={
                  reservationForm.room_type_id
                }

                onChange={(
                  event
                ) => {
                  const selectedId =
                    event.target.value;

                  const selectedType =
                    roomTypes.find(
                      (
                        roomType
                      ) =>
                        Number(
                          roomType.id
                        ) ===
                        Number(
                          selectedId
                        )
                    );

                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      room_type_id:
                        selectedId,

                      room_id: "",

                      nightly_rate:
                        previous.nightly_rate ||
                        selectedType
                          ?.base_price ||
                        "",
                    })
                  );
                }}

                sx={
                  inputStyle
                }
              >
                {roomTypes.map(
                  (
                    roomType
                  ) => (
                    <MenuItem
                      key={
                        roomType.id
                      }

                      value={
                        roomType.id
                      }
                    >
                      {roomTypeName(
                        roomType
                      )}
                      {" · "}
                      {money(
                        roomType.base_price,
                        roomType.currency,
                        language
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "roomNo"
                )}

                value={
                  reservationForm.room_id ||
                  ""
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      room_id:
                        event.target.value,
                    })
                  )
                }

                helperText={translate(
                  "noPhysicalRoom"
                )}

                sx={
                  inputStyle
                }
              >
                <MenuItem
                  value=""
                >
                  —
                </MenuItem>

                {rooms
                  .filter(
                    (room) =>
                      !reservationForm.room_type_id ||
                      Number(
                        room.room_type_id
                      ) ===
                        Number(
                          reservationForm.room_type_id
                        )
                  )
                  .map(
                    (room) => (
                      <MenuItem
                        key={
                          room.id
                        }

                        value={
                          room.id
                        }
                      >
                        {room.room_no}
                        {" · "}
                        {translate(
                          room.status ===
                            "maintenance"
                            ? "maintenanceStatus"
                            : room.status
                        )}
                      </MenuItem>
                    )
                  )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "status"
                )}

                value={
                  reservationForm.status
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      status:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {[
                  "inquiry",
                  "option",
                  "pending",
                  "confirmed",
                ].map(
                  (status) => (
                    <MenuItem
                      key={
                        status
                      }

                      value={
                        status
                      }
                    >
                      {translate(
                        status
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                fullWidth

                type="date"

                label={translate(
                  "checkIn"
                )}

                value={
                  reservationForm.check_in
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      check_in:
                        event.target.value,
                    })
                  )
                }

                InputLabelProps={{
                  shrink: true,
                }}

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                fullWidth

                type="date"

                label={translate(
                  "checkOut"
                )}

                value={
                  reservationForm.check_out
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      check_out:
                        event.target.value,
                    })
                  )
                }

                InputLabelProps={{
                  shrink: true,
                }}

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={4}
              md={2}
            >
              <TextField
                fullWidth

                type="number"

                label={translate(
                  "adults"
                )}

                value={
                  reservationForm.adults
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      adults:
                        event.target.value,
                    })
                  )
                }

                inputProps={{
                  min: 1,
                }}

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={4}
              md={2}
            >
              <TextField
                fullWidth

                type="number"

                label={translate(
                  "children"
                )}

                value={
                  reservationForm.children
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      children:
                        event.target.value,
                    })
                  )
                }

                inputProps={{
                  min: 0,
                }}

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={4}
              md={2}
            >
              <TextField
                fullWidth

                type="number"

                label={translate(
                  "infants"
                )}

                value={
                  reservationForm.infants
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      infants:
                        event.target.value,
                    })
                  )
                }

                inputProps={{
                  min: 0,
                }}

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={4}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "source"
                )}

                value={
                  reservationForm.source
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      source:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {RESERVATION_SOURCES.map(
                  (source) => (
                    <MenuItem
                      key={
                        source
                      }

                      value={
                        source
                      }
                    >
                      {translate(
                        source ===
                          "website"
                          ? "websiteSource"
                          : source ===
                            "phone"
                          ? "phoneSource"
                          : source
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={4}
            >
              <TextField
                fullWidth

                type="number"

                label={translate(
                  "nightlyRate"
                )}

                value={
                  reservationForm.nightly_rate
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      nightly_rate:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={4}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "currency"
                )}

                value={
                  reservationForm.currency
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      currency:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {CURRENCIES.map(
                  (
                    currency
                  ) => (
                    <MenuItem
                      key={
                        currency
                      }

                      value={
                        currency
                      }
                    >
                      {currency}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label={translate(
                  "specialRequests"
                )}

                value={
                  reservationForm.special_requests
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      special_requests:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label={translate(
                  "notes"
                )}

                value={
                  reservationForm.internal_notes
                }

                onChange={(
                  event
                ) =>
                  setReservationForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      internal_notes:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>
          </ResponsiveGrid>
        </DialogContent>

        <DialogActions
          sx={{
            padding: 2,
          }}
        >
          <Button
            onClick={() =>
              setReservationDialog(
                false
              )
            }
          >
            {translate(
              "cancel"
            )}
          </Button>

          <Button
            variant=
              "contained"

            onClick={
              saveReservation
            }

            sx={{
              backgroundColor:
                COLORS.orange,

              fontWeight:
                1000,
            }}
          >
            {translate(
              "save"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
          ODA DİYALOĞU
      ======================================================== */}

      <Dialog
        open={
          roomDialog
        }

        onClose={() =>
          setRoomDialog(
            false
          )
        }

        fullWidth

        maxWidth="sm"

        fullScreen={
          mobile
        }
      >
        <DialogTitle
          fontWeight={1000}
        >
          {translate(
            "newRoom"
          )}
        </DialogTitle>

        <DialogContent
          dividers
        >
          <ResponsiveGrid
            container
            spacing={2}
          >
            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "roomNo"
                )}

                value={
                  roomForm.room_no
                }

                onChange={(
                  event
                ) =>
                  setRoomForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      room_no:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "roomType"
                )}

                value={
                  roomForm.room_type_id
                }

                onChange={(
                  event
                ) =>
                  setRoomForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      room_type_id:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {roomTypes.map(
                  (
                    roomType
                  ) => (
                    <MenuItem
                      key={
                        roomType.id
                      }

                      value={
                        roomType.id
                      }
                    >
                      {roomTypeName(
                        roomType
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "floor"
                )}

                value={
                  roomForm.floor_name
                }

                onChange={(
                  event
                ) =>
                  setRoomForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      floor_name:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "building"
                )}

                value={
                  roomForm.building_name
                }

                onChange={(
                  event
                ) =>
                  setRoomForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      building_name:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "status"
                )}

                value={
                  roomForm.status
                }

                onChange={(
                  event
                ) =>
                  setRoomForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      status:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {ROOM_STATUSES.map(
                  (status) => (
                    <MenuItem
                      key={
                        status
                      }

                      value={
                        status
                      }
                    >
                      {translate(
                        status ===
                          "maintenance"
                          ? "maintenanceStatus"
                          : status
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label="Dahili Telefon"

                value={
                  roomForm.phone_extension ||
                  ""
                }

                onChange={(
                  event
                ) =>
                  setRoomForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      phone_extension:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label={translate(
                  "notes"
                )}

                value={
                  roomForm.notes ||
                  ""
                }

                onChange={(
                  event
                ) =>
                  setRoomForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      notes:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>
          </ResponsiveGrid>
        </DialogContent>

        <DialogActions
          sx={{
            padding: 2,
          }}
        >
          <Button
            onClick={() =>
              setRoomDialog(
                false
              )
            }
          >
            {translate(
              "cancel"
            )}
          </Button>

          <Button
            variant=
              "contained"

            onClick={
              saveRoom
            }

            sx={{
              backgroundColor:
                COLORS.orange,
            }}
          >
            {translate(
              "save"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
          ODA TİPİ DİYALOĞU
      ======================================================== */}

      <Dialog
        open={
          roomTypeDialog
        }

        onClose={() =>
          setRoomTypeDialog(
            false
          )
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          mobile
        }
      >
        <DialogTitle
          fontWeight={1000}
        >
          {translate(
            "newRoomType"
          )}
        </DialogTitle>

        <DialogContent
          dividers
        >
          <ResponsiveGrid
            container
            spacing={2}
          >
            <ResponsiveGrid
              xs={12}
              md={4}
            >
              <TextField
                fullWidth

                label={translate(
                  "code"
                )}

                value={
                  roomTypeForm.code
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      code:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={8}
            >
              <TextField
                fullWidth

                label={translate(
                  "nameTr"
                )}

                value={
                  roomTypeForm.name_tr
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      name_tr:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "nameEn"
                )}

                value={
                  roomTypeForm.name_en
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      name_en:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "nameDe"
                )}

                value={
                  roomTypeForm.name_de
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      name_de:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label="Türkçe Açıklama"

                value={
                  roomTypeForm.description_tr
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      description_tr:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label="English Description"

                value={
                  roomTypeForm.description_en
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      description_en:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label="Deutsche Beschreibung"

                value={
                  roomTypeForm.description_de
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      description_de:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                fullWidth

                type="number"

                label={translate(
                  "basePrice"
                )}

                value={
                  roomTypeForm.base_price
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      base_price:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "currency"
                )}

                value={
                  roomTypeForm.currency
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      currency:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {CURRENCIES.map(
                  (
                    currency
                  ) => (
                    <MenuItem
                      key={
                        currency
                      }

                      value={
                        currency
                      }
                    >
                      {currency}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                fullWidth

                type="number"

                label={translate(
                  "capacity"
                )}

                value={
                  roomTypeForm.max_occupancy
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      max_occupancy:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                fullWidth

                label="Yatak Tipi"

                value={
                  roomTypeForm.bed_type
                }

                onChange={(
                  event
                ) =>
                  setRoomTypeForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      bed_type:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>
          </ResponsiveGrid>
        </DialogContent>

        <DialogActions
          sx={{
            padding: 2,
          }}
        >
          <Button
            onClick={() =>
              setRoomTypeDialog(
                false
              )
            }
          >
            {translate(
              "cancel"
            )}
          </Button>

          <Button
            variant=
              "contained"

            onClick={
              saveRoomType
            }

            sx={{
              backgroundColor:
                COLORS.orange,
            }}
          >
            {translate(
              "save"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
          MİSAFİR DİYALOĞU
      ======================================================== */}

      <Dialog
        open={
          guestDialog
        }

        onClose={() =>
          setGuestDialog(
            false
          )
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          mobile
        }
      >
        <DialogTitle
          fontWeight={1000}
        >
          {translate(
            "newGuest"
          )}
        </DialogTitle>

        <DialogContent
          dividers
        >
          <ResponsiveGrid
            container
            spacing={2}
          >
            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "firstName"
                )}

                value={
                  guestForm.first_name
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      first_name:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "lastName"
                )}

                value={
                  guestForm.last_name
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      last_name:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "phone"
                )}

                value={
                  guestForm.phone
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      phone:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "email"
                )}

                value={
                  guestForm.email
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      email:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                fullWidth

                label={translate(
                  "country"
                )}

                value={
                  guestForm.country_code
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      country_code:
                        event.target.value.toUpperCase(),
                    })
                  )
                }

                inputProps={{
                  maxLength: 2,
                }}

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                fullWidth

                label={translate(
                  "nationality"
                )}

                value={
                  guestForm.nationality
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      nationality:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "identityType"
                )}

                value={
                  guestForm.identity_type ||
                  "passport"
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      identity_type:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                <MenuItem
                  value=
                    "identity"
                >
                  ID
                </MenuItem>

                <MenuItem
                  value=
                    "passport"
                >
                  Passport
                </MenuItem>

                <MenuItem
                  value=
                    "other"
                >
                  Other
                </MenuItem>
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                fullWidth

                type="date"

                label={translate(
                  "birthDate"
                )}

                value={
                  guestForm.birth_date ||
                  ""
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      birth_date:
                        event.target.value,
                    })
                  )
                }

                InputLabelProps={{
                  shrink: true,
                }}

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "identityNo"
                )}

                value={
                  guestForm.identity_no
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      identity_no:
                        event.target.value,
                    })
                  )
                }

                helperText={
                  guestForm.id
                    ? "Boş bırakırsanız mevcut şifreli bilgi korunur."
                    : ""
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "passportNo"
                )}

                value={
                  guestForm.passport_no
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      passport_no:
                        event.target.value,
                    })
                  )
                }

                helperText={
                  guestForm.id
                    ? "Boş bırakırsanız mevcut şifreli bilgi korunur."
                    : ""
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "language"
                )}

                value={
                  guestForm.preferred_language
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      preferred_language:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {[
                  "tr",
                  "en",
                  "de",
                  "ar",
                  "fr",
                  "it",
                  "ru",
                  "es",
                ].map(
                  (item) => (
                    <MenuItem
                      key={
                        item
                      }

                      value={
                        item
                      }
                    >
                      {item.toUpperCase()}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
              md={3}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "vip"
                )}

                value={
                  guestForm.vip_level
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      vip_level:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {[
                  "standard",
                  "silver",
                  "gold",
                  "platinum",
                  "vip",
                ].map(
                  (item) => (
                    <MenuItem
                      key={
                        item
                      }

                      value={
                        item
                      }
                    >
                      {item.toUpperCase()}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "allergies"
                )}

                value={
                  guestForm.allergy_info
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      allergy_info:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label={translate(
                  "preferences"
                )}

                value={
                  guestForm.preferences
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      preferences:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label={translate(
                  "specialRequests"
                )}

                value={
                  guestForm.special_requests
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      special_requests:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label={translate(
                  "notes"
                )}

                value={
                  guestForm.notes
                }

                onChange={(
                  event
                ) =>
                  setGuestForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      notes:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>
          </ResponsiveGrid>
        </DialogContent>

        <DialogActions
          sx={{
            padding: 2,
          }}
        >
          <Button
            onClick={() =>
              setGuestDialog(
                false
              )
            }
          >
            {translate(
              "cancel"
            )}
          </Button>

          <Button
            variant=
              "contained"

            onClick={
              saveGuest
            }

            sx={{
              backgroundColor:
                COLORS.orange,
            }}
          >
            {translate(
              "save"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
          HOUSEKEEPING DİYALOĞU
      ======================================================== */}

      <Dialog
        open={
          housekeepingDialog
        }

        onClose={() =>
          setHousekeepingDialog(
            false
          )
        }

        fullWidth

        maxWidth="sm"

        fullScreen={
          mobile
        }
      >
        <DialogTitle
          fontWeight={1000}
        >
          {translate(
            "newTask"
          )}
        </DialogTitle>

        <DialogContent
          dividers
        >
          <ResponsiveGrid
            container
            spacing={2}
          >
            <ResponsiveGrid
              xs={12}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "roomNo"
                )}

                value={
                  housekeepingForm.room_id
                }

                onChange={(
                  event
                ) =>
                  setHousekeepingForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      room_id:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {rooms.map(
                  (room) => (
                    <MenuItem
                      key={
                        room.id
                      }

                      value={
                        room.id
                      }
                    >
                      {room.room_no}
                      {" · "}
                      {room[
                        `room_type_name_${language}`
                      ] ||
                        room.room_type_name_tr ||
                        ""}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
            >
              <TextField
                fullWidth

                type="date"

                label={translate(
                  "taskDate"
                )}

                value={
                  housekeepingForm.task_date
                }

                onChange={(
                  event
                ) =>
                  setHousekeepingForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      task_date:
                        event.target.value,
                    })
                  )
                }

                InputLabelProps={{
                  shrink: true,
                }}

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "priority"
                )}

                value={
                  housekeepingForm.priority
                }

                onChange={(
                  event
                ) =>
                  setHousekeepingForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      priority:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {PRIORITIES.map(
                  (
                    priority
                  ) => (
                    <MenuItem
                      key={
                        priority
                      }

                      value={
                        priority
                      }
                    >
                      {translate(
                        priority
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "taskType"
                )}

                value={
                  housekeepingForm.task_type
                }

                onChange={(
                  event
                ) =>
                  setHousekeepingForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      task_type:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {HOUSEKEEPING_TYPES.map(
                  (
                    taskType
                  ) => (
                    <MenuItem
                      key={
                        taskType
                      }

                      value={
                        taskType
                      }
                    >
                      {translate(
                        taskType
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                label={translate(
                  "assignedTo"
                )}

                value={
                  housekeepingForm.assigned_name
                }

                onChange={(
                  event
                ) =>
                  setHousekeepingForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      assigned_name:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                multiline

                minRows={2}

                label={translate(
                  "notes"
                )}

                value={
                  housekeepingForm.notes
                }

                onChange={(
                  event
                ) =>
                  setHousekeepingForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      notes:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>
          </ResponsiveGrid>
        </DialogContent>

        <DialogActions
          sx={{
            padding: 2,
          }}
        >
          <Button
            onClick={() =>
              setHousekeepingDialog(
                false
              )
            }
          >
            {translate(
              "cancel"
            )}
          </Button>

          <Button
            variant=
              "contained"

            onClick={
              saveHousekeepingTask
            }

            sx={{
              backgroundColor:
                COLORS.orange,
            }}
          >
            {translate(
              "save"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
          TEKNİK SERVİS DİYALOĞU
      ======================================================== */}

      <Dialog
        open={
          maintenanceDialog
        }

        onClose={() =>
          setMaintenanceDialog(
            false
          )
        }

        fullWidth

        maxWidth="sm"

        fullScreen={
          mobile
        }
      >
        <DialogTitle
          fontWeight={1000}
        >
          {translate(
            "newMaintenance"
          )}
        </DialogTitle>

        <DialogContent
          dividers
        >
          <ResponsiveGrid
            container
            spacing={2}
          >
            <ResponsiveGrid
              xs={12}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "roomNo"
                )}

                value={
                  maintenanceForm.room_id
                }

                onChange={(
                  event
                ) =>
                  setMaintenanceForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      room_id:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                <MenuItem
                  value=""
                >
                  —
                </MenuItem>

                {rooms.map(
                  (room) => (
                    <MenuItem
                      key={
                        room.id
                      }

                      value={
                        room.id
                      }
                    >
                      {room.room_no}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                label={translate(
                  "titleField"
                )}

                value={
                  maintenanceForm.title
                }

                onChange={(
                  event
                ) =>
                  setMaintenanceForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      title:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "category"
                )}

                value={
                  maintenanceForm.category
                }

                onChange={(
                  event
                ) =>
                  setMaintenanceForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      category:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={6}
            >
              <TextField
                select
                fullWidth

                label={translate(
                  "priority"
                )}

                value={
                  maintenanceForm.priority
                }

                onChange={(
                  event
                ) =>
                  setMaintenanceForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      priority:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              >
                {PRIORITIES.map(
                  (
                    priority
                  ) => (
                    <MenuItem
                      key={
                        priority
                      }

                      value={
                        priority
                      }
                    >
                      {translate(
                        priority
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                label={translate(
                  "assignedTo"
                )}

                value={
                  maintenanceForm.assigned_name
                }

                onChange={(
                  event
                ) =>
                  setMaintenanceForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      assigned_name:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                type="number"

                label={translate(
                  "estimatedCost"
                )}

                value={
                  maintenanceForm.estimated_cost
                }

                onChange={(
                  event
                ) =>
                  setMaintenanceForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      estimated_cost:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <Paper
                variant=
                  "outlined"

                sx={{
                  padding:
                    1.5,

                  borderRadius:
                    3,

                  borderColor:
                    COLORS.border,
                }}
              >
                <Stack
                  direction=
                    "row"

                  justifyContent=
                    "space-between"

                  alignItems=
                    "center"
                >
                  <Typography
                    fontWeight={
                      900
                    }
                  >
                    {translate(
                      "roomBlocked"
                    )}
                  </Typography>

                  <Switch
                    checked={
                      Boolean(
                        maintenanceForm.room_blocked
                      )
                    }

                    onChange={(
                      event
                    ) =>
                      setMaintenanceForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          room_blocked:
                            event.target.checked,
                        })
                      )
                    }
                  />
                </Stack>
              </Paper>
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
            >
              <TextField
                fullWidth

                multiline

                minRows={3}

                label={translate(
                  "description"
                )}

                value={
                  maintenanceForm.description
                }

                onChange={(
                  event
                ) =>
                  setMaintenanceForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      description:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>
          </ResponsiveGrid>
        </DialogContent>

        <DialogActions
          sx={{
            padding: 2,
          }}
        >
          <Button
            onClick={() =>
              setMaintenanceDialog(
                false
              )
            }
          >
            {translate(
              "cancel"
            )}
          </Button>

          <Button
            variant=
              "contained"

            onClick={
              saveMaintenanceIssue
            }

            sx={{
              backgroundColor:
                COLORS.orange,
            }}
          >
            {translate(
              "save"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
          FOLYO DİYALOĞU
      ======================================================== */}

      <Dialog
        open={
          folioDialog
        }

        onClose={() =>
          setFolioDialog(
            false
          )
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          mobile
        }
      >
        <DialogTitle
          fontWeight={1000}
        >
          {translate(
            "folio"
          )}
          {" · "}
          {folioReservation
            ?.reservation_no}
        </DialogTitle>

        <DialogContent
          dividers
        >
          <ResponsiveGrid
            container
            spacing={2}

            sx={{
              marginBottom:
                2,
            }}
          >
            <ResponsiveGrid
              xs={4}
            >
              <KpiCard
                icon="🧾"

                label={translate(
                  "total"
                )}

                value={money(
                  folio.reservation
                    ?.grand_total ||
                  folioReservation
                    ?.grand_total,

                  folioReservation
                    ?.currency,

                  language
                )}
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={4}
            >
              <KpiCard
                icon="✅"

                label={translate(
                  "paid"
                )}

                value={money(
                  folio.reservation
                    ?.paid_total ||
                  folioReservation
                    ?.paid_total,

                  folioReservation
                    ?.currency,

                  language
                )}

                accent={
                  COLORS.green
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={4}
            >
              <KpiCard
                icon="⚠️"

                label={translate(
                  "balance"
                )}

                value={money(
                  folio.reservation
                    ?.balance_total ||
                  folioReservation
                    ?.balance_total,

                  folioReservation
                    ?.currency,

                  language
                )}

                accent={
                  COLORS.red
                }
              />
            </ResponsiveGrid>
          </ResponsiveGrid>

          <Tabs
            value={
              folioMode
            }

            onChange={(
              _event,
              value
            ) =>
              setFolioMode(
                value
              )
            }

            sx={{
              marginBottom: 2,

              "& .MuiTabs-indicator":
                {
                  backgroundColor:
                    COLORS.orange,
                },
            }}
          >
            <Tab
              value="charge"

              label={translate(
                "charge"
              )}
            />

            <Tab
              value="payment"

              label={translate(
                "payment"
              )}
            />
          </Tabs>

          <ResponsiveGrid
            container
            spacing={2}
          >
            <ResponsiveGrid
              xs={12}
              md={6}
            >
              <TextField
                fullWidth

                label={translate(
                  "description"
                )}

                value={
                  folioForm.description
                }

                onChange={(
                  event
                ) =>
                  setFolioForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      description:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            <ResponsiveGrid
              xs={12}
              md={3}
            >
              <TextField
                fullWidth

                type="number"

             label={
  folioMode ===
  "charge"
    ? "Birim Fiyat"
    : translate(
        "amount"
      )
}

                value={
                  folioForm.amount
                }

                onChange={(
                  event
                ) =>
                  setFolioForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      amount:
                        event.target.value,
                    })
                  )
                }

                sx={
                  inputStyle
                }
              />
            </ResponsiveGrid>

            {folioMode ===
              "payment" && (
              <ResponsiveGrid
                xs={12}
                md={3}
              >
                <TextField
                  select
                  fullWidth

                  label={translate(
                    "paymentMethod"
                  )}

                  value={
                    folioForm.payment_method
                  }

                  onChange={(
                    event
                  ) =>
                    setFolioForm(
                      (
                        previous
                      ) => ({
                        ...previous,

                        payment_method:
                          event.target.value,
                      })
                    )
                  }

                  sx={
                    inputStyle
                  }
                >
                  {PAYMENT_METHODS.map(
                    (
                      method
                    ) => (
                      <MenuItem
                        key={
                          method
                        }

                        value={
                          method
                        }
                      >
                        {translate(
                          method
                        )}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </ResponsiveGrid>
            )}

            <ResponsiveGrid
              xs={12}
            >
              <Button
                variant=
                  "contained"

                onClick={
                  saveFolioEntry
                }

                sx={{
                  backgroundColor:
                    COLORS.orange,

                  borderRadius:
                    99,

                  fontWeight:
                    1000,
                }}
              >
                {folioMode ===
                "charge"
                  ? translate(
                      "charge"
                    )
                  : translate(
                      "payment"
                    )}
              </Button>
            </ResponsiveGrid>
          </ResponsiveGrid>

          <Divider
            sx={{
              marginY: 2,
            }}
          />

          <Typography
            fontWeight={1000}

            sx={{
              marginBottom: 1,
            }}
          >
            {translate(
              "charge"
            )}
          </Typography>

          {(folio.items ||
            []).map(
            (item) => (
              <Stack
                key={
                  item.id
                }

                direction=
                  "row"

                justifyContent=
                  "space-between"

                spacing={1}

                sx={{
                  paddingY:
                    0.9,

                  borderBottom:
                    `1px solid ${COLORS.border}`,
                }}
              >
                <Box
                  sx={{
                    minWidth:
                      0,
                  }}
                >
                  <Typography>
                    {item.description}
                  </Typography>

                  <Typography
                    sx={{
                      color:
                        COLORS.muted,

                      fontSize:
                        12,
                    }}
                  >
                    {normalizeDate(
                      item.item_date
                    )}
                    {" · "}
                    {item.quantity}
                    {" × "}
                    {money(
                      item.unit_price,
                      folioReservation
                        ?.currency,

                      language
                    )}
                  </Typography>
                </Box>

                <Typography
                  fontWeight={900}

                  noWrap
                >
                  {money(
                    item.total_amount,
                    folioReservation
                      ?.currency,

                    language
                  )}
                </Typography>
              </Stack>
            )
          )}

          {(folio.items ||
            []).length ===
            0 && (
            <EmptyState
              translate={
                translate
              }
            />
          )}

          <Typography
            fontWeight={1000}

            sx={{
              marginTop: 2,
              marginBottom: 1,
            }}
          >
            {translate(
              "payment"
            )}
          </Typography>

          {(folio.payments ||
            []).map(
            (payment) => (
              <Stack
                key={
                  payment.id
                }

                direction=
                  "row"

                justifyContent=
                  "space-between"

                spacing={1}

                sx={{
                  paddingY:
                    0.9,

                  borderBottom:
                    `1px solid ${COLORS.border}`,
                }}
              >
                <Box>
                  <Typography>
                    {translate(
                      payment.payment_method
                    )}
                  </Typography>

                  <Typography
                    sx={{
                      color:
                        COLORS.muted,

                      fontSize:
                        12,
                    }}
                  >
                    {normalizeDate(
                      payment.payment_date
                    )}
                    {" · "}
                    {payment.payment_no}
                  </Typography>
                </Box>

                <Typography
                  fontWeight={900}

                  noWrap

                  sx={{
                    color:
                      COLORS.green,
                  }}
                >
                  {money(
                    payment.amount,
                    folioReservation
                      ?.currency,

                    language
                  )}
                </Typography>
              </Stack>
            )
          )}

          {(folio.payments ||
            []).length ===
            0 && (
            <EmptyState
              translate={
                translate
              }
            />
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: 2,
          }}
        >
          <Button
            onClick={() =>
              setFolioDialog(
                false
              )
            }
          >
            {translate(
              "close"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================
          BİLDİRİM
      ======================================================== */}

      <Snackbar
        open={
          notification.open
        }

        autoHideDuration={
          3500
        }

        onClose={() =>
          setNotification(
            (
              previous
            ) => ({
              ...previous,
              open: false,
            })
          )
        }

        anchorOrigin={{
          vertical:
            "bottom",

          horizontal:
            "center",
        }}
      >
        <Alert
          severity={
            notification.severity
          }

          onClose={() =>
            setNotification(
              (
                previous
              ) => ({
                ...previous,
                open: false,
              })
            )
          }

          sx={{
            borderRadius:
              3,

            fontWeight:
              900,

            maxWidth:
              "calc(100vw - 24px)",
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
